import React, { useState, useEffect, useRef } from 'react';
import { useNotifier } from '../ui/Notifier';
import { ConfirmDialog } from '../ui/ConfirmDialog';
import type { ApoliceData } from '../../services/apoliceService';
import { documentoService, seguradoraService, produtoService, formaPagamentoService, situacaoService, statusService, parcelaService } from '../../services/entitiesServices';
import { apiClient } from '../../config/api';
import { repasseService } from '../../services/repasseService';
import { sinistroService } from '../../services/sinistroService';
import AgendamentoFormModal from '../../components/modals/AgendamentoFormModal';
import SinistroModal from '../sinistro/SinistroModal';
import type { Parcela } from '../../types/entities';
import { toSaoPauloTimestamp } from '../../utils/formatters';

// Helper: normalize various date formats into YYYY-MM-DD for <input type="date">
const toInputDate = (value: any): string => {
	if (!value) return '';
	if (typeof value === 'string') {
		// ISO string (2024-08-25T00:00:00Z) or already YYYY-MM-DD
		if (/^\d{4}-\d{2}-\d{2}/.test(value)) return value.slice(0, 10);
		// DD/MM/YYYY -> convert
		if (/^\d{2}\/\d{2}\/\d{4}$/.test(value)) {
			const [d, m, y] = value.split('/');
			return `${y}-${m.padStart(2, '0')}-${d.padStart(2, '0')}`;
		}
		// Fallback: try Date parse
		const dt = new Date(value);
		if (!Number.isNaN(dt.getTime())) {
			return dt.toISOString().slice(0, 10);
		}
	}
	if (value instanceof Date) return value.toISOString().slice(0, 10);
	return '';
};

// Safe number formatter: ensures value is a number before calling toFixed
const formatNumber = (v: any, decimals = 2) => {
	if (v === null || v === undefined || v === '') return (0).toFixed(decimals);
	const n = typeof v === 'number' ? v : parseFloat(String(v).replace(/[^0-9.-]+/g, ''));
	if (Number.isNaN(n)) return (0).toFixed(decimals);
	return n.toFixed(decimals);
};

// Parser de moeda para número
const parseCurrency = (value: string): string => {
	if (!value) return '';
	// Remove símbolos de moeda e espaços, mantém apenas números, vírgulas e pontos
	const cleanValue = value.replace(/[R$\s]/g, '').replace(/\./g, '').replace(',', '.');
	return cleanValue;
};

interface ApoliceFormModalProps {
	apolice?: ApoliceData;
	isOpen: boolean;
	isEditing?: boolean;
	onClose: () => void;
	onSave: (apolice: ApoliceData) => void;
	onUpdated?: (apolice: ApoliceData) => void;
	onNextRequested?: (currentId?: string) => Promise<ApoliceData | undefined> | ApoliceData | undefined;
    // When true, indicates the modal was opened from the Renovação card
    // and renovacao-specific flags should be visible.
    isFromRenovationCard?: boolean;
	// when true, scroll the Parcelas section into view after the modal opens
	openParcelasOnOpen?: boolean;
}

type ParcelaDetalhe = {
	vencer?: string;
	vencido?: string;
	proposta?: string;
	apolice?: string;
	endosso?: string;
	sequencia?: string;
	data?: string; // vencimento date
	pago?: boolean;
};

interface FormData {
	// canonical checkbox flags (as expected by backend)
	propostaEnviada?: boolean;
	boletoEnviado?: boolean;
	apoliceEnviada?: boolean;
	kitEnviado?: boolean;
	renovacaoEnviada?: boolean;
	renovacaoAprovada?: boolean;
	marcarEnvioApolice?: boolean;
	marcarEnvioProposta?: boolean;
	exigeAnaliseTecnica?: boolean;
	exigeVistoria?: boolean;

	// newly requested boolean flags
	renovacaoMensagem?: boolean;
	boasvindasMensagem?: boolean;
	propostaNaPasta?: boolean;
	propostaZapsign?: boolean;

	// additional domain flags (kept for completeness)
	agendar?: boolean;
	renovacaoDuplicada?: boolean;
	baixaSistema?: boolean;
	baixaRelatorio?: boolean;
	baixaConsultaProposta?: boolean;
	novaRenovacao?: boolean;
	naoRenovavel?: boolean;
	propostaArquivada?: boolean;
	propostaConferida?: boolean;

	// textual fields
	status: string;
	situacao: string;
	proposta: string;
	calculoCol: string;
	numeroDocumento: string;
	propostaCia: string;
	dataProsposta: string;
	dataEntrada: string;
	inicioVigencia: string;
	terminoVigencia: string;
	endosso: string;
	apolice: string;
	seguradora: string | number;
	produto: string | number;
	premioLiquido: string;
	adicional: string; // kept as string for free-text adicional
	comissaoPercent: string;
	comissaoValor: string;

	cpfCnpj: string;
	nome: string;
	email: string;
	telefone: string;
	idDigisac: string;
	controle: string; // free-text controle
	controleInterno?: boolean; // boolean flag in backend
	empresa?: string; // nome da empresa onde o documento pertence (útil para grupos)

	informacoesComplementares: string;
	dataCancel: string;
	motivo: string;

	parcelas: string;
	pagamentoId: string | number | undefined;
	parcelasDetalhes: ParcelaDetalhe[];
}

export const ApoliceFormModal: React.FC<ApoliceFormModalProps> = ({ apolice, isOpen, isEditing = false, onClose, onSave, onUpdated, onNextRequested, isFromRenovationCard = false, openParcelasOnOpen = false }) => {
	const [formData, setFormData] = useState<FormData>({
		// requested flags defaults (keep canonical names)
		propostaEnviada: false,
		boletoEnviado: false,
		apoliceEnviada: true,
		kitEnviado: false,
		renovacaoEnviada: false,
		renovacaoAprovada: false,
		marcarEnvioApolice: false,
		marcarEnvioProposta: false,
		exigeAnaliseTecnica: false,
		exigeVistoria: false,
		// newly requested flags defaults
		renovacaoMensagem: false,
		boasvindasMensagem: false,
		propostaNaPasta: false,
		propostaZapsign: true,
		// other domain flags default to false/undefined
		agendar: false,
		renovacaoDuplicada: false,
		baixaSistema: false,
		baixaRelatorio: false,
		baixaConsultaProposta: false,
		novaRenovacao: false,
		naoRenovavel: false,
	propostaArquivada: false,
	propostaConferida: false,
		controleInterno: false,

		status: 'Proposta',
		situacao: 'Ativa',
		proposta: '',
		calculoCol: '',
		numeroDocumento: '',
		propostaCia: '',
		dataProsposta: '',
		dataEntrada: '',
		inicioVigencia: '',
		terminoVigencia: '',
		endosso: '',
		apolice: '',
		seguradora: '',
		produto: '',
		premioLiquido: '',
	adicional: '',
	comissaoPercent: '0',
	comissaoValor: '0',

		cpfCnpj: '',
		nome: '',
		email: '',
		telefone: '',
		idDigisac: '',
		controle: '',
		empresa: '',

		informacoesComplementares: '',
		dataCancel: '',
		motivo: '',

		parcelas: '1',
		pagamentoId: undefined,
		parcelasDetalhes: [{ sequencia: '1', data: '' }],
	});
	const [originalSource, setOriginalSource] = useState<any>(null);
	const [originalSnapshot, setOriginalSnapshot] = useState<string | null>(null);
	const [catalogs, setCatalogs] = useState<{ seguradoras: any[]; produtos: any[]; pagamentos: any[]; situacoes: any[]; status: any[] }>(
		{ seguradoras: [], produtos: [], pagamentos: [], situacoes: [], status: [] }
	);

	// Parcelas carregadas a partir do endpoint (lista de parcelas já criadas para o documento)
	const [parcelasApi, setParcelasApi] = useState<Parcela[]>([]);

	// ref for Parcelas section to allow scrolling into view when opened from Parcela page
	const parcelasSectionRef = useRef<HTMLDivElement | null>(null);
	// inline edit state: track parcela id currently being edited and a draft object
	const [editingParcelaId, setEditingParcelaId] = useState<string | number | null>(null);
	const [parcelaDraft, setParcelaDraft] = useState<Partial<Parcela> | null>(null);

	// per-row saving map: { [parcelaId]: true } while that parcela is being saved
	const [savingParcelaById, setSavingParcelaById] = useState<Record<string | number, boolean>>({});

	// Repasses carregados a partir do endpoint
	const [repassesApi, setRepassesApi] = useState<any[]>([]);
	// inline edit state para repasses
	const [editingRepasseId, setEditingRepasseId] = useState<string | number | null>(null);
	const [repasseDraft, setRepasseDraft] = useState<any | null>(null);

	// per-row saving map para repasses
	const [savingRepasseById, setSavingRepasseById] = useState<Record<string | number, boolean>>({});

	// Sinistros carregados a partir do endpoint
	const [sinistrosApi, setSinistrosApi] = useState<any[]>([]);

	// Estados para dialogs de confirmação
	const [showDeleteRepasseConfirm, setShowDeleteRepasseConfirm] = useState(false);
	const [repasseToDelete, setRepasseToDelete] = useState<any>(null);
	const [showDeleteParcelaConfirm, setShowDeleteParcelaConfirm] = useState(false);
	const [parcelaToDelete, setParcelaToDelete] = useState<Parcela | null>(null);
	// Confirm dialog when toggling parcela from paga -> aberta
	const [showConfirmAbrirParcela, setShowConfirmAbrirParcela] = useState(false);
	const [parcelaToToggleOpen, setParcelaToToggleOpen] = useState<Parcela | null>(null);

	// Estado para modal de criação de sinistro
	const [showNovoSinistroModal, setShowNovoSinistroModal] = useState(false);
	const [novoSinistroDraft, setNovoSinistroDraft] = useState({
		numeroDocumento: '',
		apolice: '',
		endosso: '',
		cpf_cnpj: '',
		nome: '',
		data: '',
		informacoes: '',
		tipo: '',
		valor_franquia: '',
		numero_sinistro: '',
		documentoId: '',
		oficina: false,
		abertura: false,
		documentos: false,
		vistoria_sinistro: false,
		analise: false,
		aprovacao: false,
		pagamento: false,
		concluido: false
	});

	// Estado para modal de edição de sinistro
	const [showEditSinistroModal, setShowEditSinistroModal] = useState(false);
	const [editSinistroDraft, setEditSinistroDraft] = useState<any>(null);

	// Estado para modal de criação de parcelas
	const [showNovaParcelaModal, setShowNovaParcelaModal] = useState(false);
	const [novaParcelaQuantidade, setNovaParcelaQuantidade] = useState<number>(1);
	// kept for backward compatibility with older references in the JSX
	const [novaParcelaValorTotal, setNovaParcelaValorTotal] = useState<number | null>(null);

	// Estado para criação de repasse inline
	const [isCreatingNewRepasse, setIsCreatingNewRepasse] = useState(false);
	const [newRepasseDraft, setNewRepasseDraft] = useState<Record<string, any>>({});

	// Additional repasse summary fields requested
	const [taxaComissao, setTaxaComissao] = useState<string>(''); // taxa da Seguradora (percentual ou valor)
	const [conta, setConta] = useState<string>(''); // Conta (comissao - taxa %)
	const [totalRepassePago, setTotalRepassePago] = useState<number>(0); // soma de repasses existentes

	// computed final value: conta - totalRepassePago
	const valorFinal = (() => {
		const c = parseFloat(String(conta).replace(/[^0-9.-]+/g, '')) || 0;
		const v = Number(totalRepassePago) || 0;
		return c - v;
	})();

	useEffect(() => {
		// recompute sum of repasses from server
		if (!repassesApi || repassesApi.length === 0) {
			setTotalRepassePago(0);
			return;
		}
		const sum = repassesApi.reduce((acc: number, r: any) => {
			const v = r && (r.repassePago ?? r.valor ?? r.valorRepasse) ? Number(r.repassePago ?? r.valor ?? r.valorRepasse) : 0;
			return acc + (Number.isNaN(v) ? 0 : v);
		}, 0);
		setTotalRepassePago(sum);
	}, [repassesApi]);

	// Estado para criação de parcela inline
	const [isCreatingNewParcela, setIsCreatingNewParcela] = useState(false);
	const [newParcelaDraft, setNewParcelaDraft] = useState<Record<string, any>>({});

	// Agendamento modal state (button in Cliente block will open this)
	const [openAgendamentoModal, setOpenAgendamentoModal] = useState(false);
	const [initialDescricaoAgendamento, setInitialDescricaoAgendamento] = useState<string | undefined>(undefined);

	const setFormFromSource = (source: any) => {
		setFormData({
			// map canonical flags from server when available
			propostaEnviada: !!source?.propostaEnviada,
			boletoEnviado: !!source?.boletoEnviado,
			apoliceEnviada: !!source?.apoliceEnviada,
			exigeAnaliseTecnica: !!source?.exigeAnaliseTecnica,
			exigeVistoria: !!source?.exigeVistoria,
			kitEnviado: !!source?.kitEnviado,
			renovacaoEnviada: !!source?.renovacaoEnviada,
			renovacaoAprovada: !!source?.renovacaoAprovada,
			marcarEnvioApolice: !!source?.marcarEnvioApolice,
			marcarEnvioProposta: !!source?.marcarEnvioProposta,
			// newly requested flags from server
			renovacaoMensagem: !!source?.renovacaoMensagem || !!source?.mensagemRenovacao,
			boasvindasMensagem: !!source?.boasvindasMensagem || !!source?.mensagemBoasVindas,
			propostaNaPasta: !!source?.propostaNaPasta,
			propostaZapsign: typeof source?.propostaZapsign === 'boolean' ? !!source?.propostaZapsign : true,

			status: source?.status?.nome || 'Proposta',
			situacao: source?.situacao?.nome || 'Ativa',
			proposta: source?.numeroDocumento || source?.codigoProposta || '',
			calculoCol: source?.numeroCalculo || '',
			numeroDocumento: source?.numeroDocumento || source?.codigoProposta || '',
			propostaCia: source?.codigoProposta || '',
			dataProsposta: toInputDate(source?.dataEmissao) || '',
			dataEntrada: toInputDate(source?.dataEntrada) || '',
			inicioVigencia: toInputDate(source?.inicioVigencia) || '',
			terminoVigencia: toInputDate(source?.fimVigencia) || '',
			endosso: source?.numeroEndosso || '',
			apolice: source?.numeroApolice || '',
			// prefer to keep the id when available, otherwise the name
			seguradora: (source?.seguradora && (source.seguradora.id || source.seguradora)) ?? (typeof source?.seguradora === 'string' ? source?.seguradora : ''),
			produto: (source?.produto && (source.produto.id || source.produto)) ?? (typeof source?.produto === 'string' ? source?.produto : ''),
			premioLiquido: source?.premioLiquido?.toString() || '',
			adicional: source?.adicional ? String(source.adicional) : '',
			comissaoPercent: source?.percentualComissao ? String(source.percentualComissao) : '0',
			comissaoValor: source?.comissao ? String(source.comissao) : '0',

			cpfCnpj: source?.cliente?.cpfCnpj || source?.cpfCnpjCliente || '',
			nome: source?.cliente?.nome || '',
			empresa: source?.empresa?.nome || (source?.empresa && source.empresa.nome) || source?.empresa || '',
			email: source?.cliente?.email || source?.email || '',
			telefone: source?.cliente?.telefone || source?.telefone || '',
			idDigisac: '',
			controle: source?.controleInterno ? String(source.controleInterno) : '',

			informacoesComplementares: source?.observacoes || '',
			dataCancel: toInputDate(source?.dataCancelamento) || '',
			motivo: source?.motivoCancelamento || '',

			parcelas: source?.quantidadeParcelas ? String(source.quantidadeParcelas) : '1',
			pagamentoId: source?.pagamentoId ?? (source?.pagamento && (source.pagamento.id || source.pagamento)) ?? (source?.formaPagamento && (source.formaPagamento.id || source.formaPagamento)) ?? undefined,
			parcelasDetalhes:
				source?.quantidadeParcelas && source.quantidadeParcelas > 0
					? Array.from({ length: source.quantidadeParcelas }).map((_, i) => ({ sequencia: String(i + 1), data: '' }))
					: [{ sequencia: '1', data: '' }],
		});

		// populate the repasse summary local states when source is provided
		try {
			// taxaComissao: prefer explicit field, then seguradora.taxaComissao
			const taxa = source?.taxaComissao ?? source?.seguradora?.taxaComissao ?? source?.taxaComissao;
			setTaxaComissao(typeof taxa !== 'undefined' && taxa !== null ? String(taxa) : '');

			// conta: prefer explicit 'conta', fallback to 'contaSeguradora' or compute from comissao - taxa (if available)
			if (typeof source?.conta !== 'undefined' && source?.conta !== null) {
				setConta(String(source.conta));
			} else if (typeof source?.contaSeguradora !== 'undefined' && source?.contaSeguradora !== null) {
				setConta(String(source.contaSeguradora));
			} else if (typeof source?.comissao !== 'undefined' && typeof taxa !== 'undefined' && taxa !== null) {
				// if taxa is percentual (e.g., 10) try to compute conta = comissao - (comissao * taxa/100)
				const c = Number(source.comissao) || 0;
				const t = Number(taxa) || 0;
				if (!Number.isNaN(c) && !Number.isNaN(t)) {
					const computed = c - (c * (t / 100));
					setConta(String(Number(computed.toFixed(2))));
				}
			}

			// totalRepassePago: prefer explicit, otherwise sum repasses if provided
			if (typeof source?.totalRepassePago !== 'undefined' && source?.totalRepassePago !== null) {
				setTotalRepassePago(Number(source.totalRepassePago) || 0);
			} else if (Array.isArray(source?.repasses) && source.repasses.length > 0) {
				const sum = source.repasses.reduce((acc: number, r: any) => acc + (Number(r.repassePago ?? r.valor ?? 0) || 0), 0);
				setTotalRepassePago(sum);
			} else {
				setTotalRepassePago(0);
			}
		} catch (e) {
			// ignore
		}
	};

	const normalizeForSnapshot = (src: any) => {
		if (!src) return '';
		const obj: any = {
			status: src?.status?.nome || '',
			proposta: src?.numeroDocumento || src?.codigoProposta || '',
			apolice: src?.numeroApolice || src?.numeroDocumento || '',
			dataProsposta: toInputDate(src?.dataEmissao) || '',
			dataEntrada: toInputDate(src?.dataEntrada) || '',
			inicioVigencia: toInputDate(src?.inicioVigencia) || '',
			terminoVigencia: toInputDate(src?.fimVigencia) || '',
			endosso: src?.numeroEndosso || '',
			// normalize to id if present, otherwise the name
			seguradora: (src?.seguradora && (src.seguradora.id ?? src.seguradora.nome ?? src.seguradora)) || '',
			produto: (src?.produto && (src.produto.id ?? src.produto.nome ?? src.produto)) || '',
			premioLiquido: typeof src?.premioLiquido !== 'undefined' ? String(Number(String(src.premioLiquido).replace(/,/g, '.'))) : '',
			comissaoValor: typeof src?.comissao !== 'undefined' ? String(Number(String(src.comissao).replace(/,/g, '.'))) : '',
			comissaoPercent: typeof src?.percentualComissao !== 'undefined' ? String(Number(String(src.percentualComissao).replace(/,/g, '.'))) : '',
			cpfCnpj: src?.cliente?.cpfCnpj || src?.cpfCnpjCliente || '',
			nome: src?.cliente?.nome || '',
			empresa: src?.empresa?.nome || (src?.empresa && src.empresa.nome) || '',
			// requested flags
			propostaEnviada: src?.propostaEnviada ? String(!!src?.propostaEnviada) : '',
			boletoEnviado: src?.boletoEnviado ? String(!!src?.boletoEnviado) : '',
			apoliceEnviada: src?.apoliceEnviada ? String(!!src?.apoliceEnviada) : '',
			exigeAnaliseTecnica: src?.exigeAnaliseTecnica ? String(!!src?.exigeAnaliseTecnica) : '',
			exigeVistoria: src?.exigeVistoria ? String(!!src?.exigeVistoria) : '',
			kitEnviado: src?.kitEnviado ? String(!!src?.kitEnviado) : '',
			renovacaoEnviada: src?.renovacaoEnviada ? String(!!src?.renovacaoEnviada) : '',
			renovacaoAprovada: src?.renovacaoAprovada ? String(!!src?.renovacaoAprovada) : '',
			marcarEnvioApolice: src?.marcarEnvioApolice ? String(!!src?.marcarEnvioApolice) : '',
			marcarEnvioProposta: src?.marcarEnvioProposta ? String(!!src?.marcarEnvioProposta) : '',
			email: src?.cliente?.email || src?.email || '',
			telefone: src?.cliente?.telefone || src?.telefone || '',
			situacao: src?.situacao?.nome || '',
			informacoesComplementares: src?.observacoes || '',
			dataCancel: toInputDate(src?.dataCancelamento) || '',
			motivo: src?.motivoCancelamento || '',
			parcelas: src?.quantidadeParcelas ? String(src.quantidadeParcelas) : '',
		};
		// trim all values to avoid whitespace diffs
		Object.keys(obj).forEach((k) => {
			if (obj[k] === null || typeof obj[k] === 'undefined') obj[k] = '';
			if (typeof obj[k] === 'string') obj[k] = obj[k].trim();
		});
		return JSON.stringify(obj);
	};

	// When the modal opens or the provided `apolice` changes, populate the form.
	useEffect(() => {
		let mounted = true;
		const populate = async () => {
			if (!isOpen) return;
			// If we have an id, prefer fetching the full documento from the API
			let source = apolice;
			try {
				if (apolice && apolice.id) {
					const resp: any = await documentoService.getById(apolice.id);
					// BaseService.getById returns ApiResponse<T> shape: { data: T }
					if (resp && resp.data) source = resp.data as any;
				}
			} catch (err) {
				// If fetch fails, fallback to provided apolice prop
				console.warn('Falha ao buscar documento completo, usando dados parciais:', err);
			}

			if (!mounted) return;
			setFormFromSource(source);
			setOriginalSource(source || null);
			
			// Carregar dados relacionados que vêm com include
			if (source) {
				// Parcelas
				if (source.parcelas && Array.isArray(source.parcelas)) {
					setParcelasApi(source.parcelas);
				} else {
					setParcelasApi([]);
				}
				
				// Repasses
				if (source.repasses && Array.isArray(source.repasses)) {
					setRepassesApi(source.repasses);
				} else {
					setRepassesApi([]);
				}
				
				// Sinistros
				if (source.sinistros && Array.isArray(source.sinistros)) {
					setSinistrosApi(source.sinistros);
				} else {
					setSinistrosApi([]);
				}
			} else {
				// Limpar dados se não houver source
				setParcelasApi([]);
				setRepassesApi([]);
				setSinistrosApi([]);
			}
			
			// store normalized snapshot for dirty-checking
			const snap = normalizeForSnapshot(source || {});
			setOriginalSnapshot(snap);
		};

		populate();

		return () => {
			mounted = false;
		};
	}, [isOpen, apolice]);

	useEffect(() => {
		if (!isOpen) return;
		if (!openParcelasOnOpen) return;
		// small delay to allow modal layout to stabilize
		const t = setTimeout(() => {
			parcelasSectionRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
		}, 150);
		return () => clearTimeout(t);
	}, [isOpen, openParcelasOnOpen]);


	useEffect(() => {
		if (!isOpen) return;
		// fetch catalogs in background; attempt to retrieve many items via service.list
		(async () => {
			let seguradoras: any[] = [];
			let produtos: any[] = [];
			let pagamentos: any[] = [];
			let situacoes: any[] = [];
			let status: any[] = [];
			try {
				seguradoras = await fetchAllFromService(seguradoraService, { limit: 200 });
				if (!seguradoras || seguradoras.length === 0) seguradoras = await fetchCatalog('/api/catalogs/seguradoras');
			} catch (e) { seguradoras = await fetchCatalog('/api/catalogs/seguradoras'); }
			try {
				produtos = await fetchAllFromService(produtoService, { limit: 200 });
				if (!produtos || produtos.length === 0) produtos = await fetchCatalog('/api/catalogs/produtos');
			} catch (e) { produtos = await fetchCatalog('/api/catalogs/produtos'); }
			try {
				const pm = await formaPagamentoService.list({ limit: 1000 });
				// service.list may return either an array or { data: [...] }
				if (Array.isArray(pm)) pagamentos = pm as any[];
				else if (pm && Array.isArray((pm as any).data)) pagamentos = (pm as any).data;
				else pagamentos = [];
			} catch (e) { pagamentos = await fetchCatalog('/api/catalogs/pagamentos'); }
			try {
				const si = await situacaoService.list({ limit: 1000 });
				situacoes = (si && (si.data || si.data === 0) && Array.isArray((si as any).data)) ? (si as any).data : [];
			} catch (e) { situacoes = await fetchCatalog('/api/catalogs/situacoes'); }
			try {
				const st = await statusService.list({ limit: 1000 });
				status = (st && (st.data || st.data === 0) && Array.isArray((st as any).data)) ? (st as any).data : [];
			} catch (e) { status = await fetchCatalog('/api/catalogs/status'); }

			// Ordenar listas alfabeticamente por nome
			seguradoras = seguradoras.sort((a, b) => (a.nome || '').localeCompare(b.nome || ''));
			produtos = produtos.sort((a, b) => (a.nome || '').localeCompare(b.nome || ''));

			// debug: log pagamentos shape to help if select appears empty in some environments
			try { console.debug('ApoliceFormModal: pagamentos fetched', { count: (pagamentos || []).length, sample: (pagamentos || [])[0] }); } catch (e) {}
			setCatalogs({ seguradoras, produtos, pagamentos, situacoes, status });
		})();
	}, [isOpen]);

	// Carrega as parcelas do servidor para o documento quando o modal abre

	// Extracted loader so other handlers (delete) can re-fetch after mutations
	// Now accepts a docId parameter and doesn't close over `apolice` or `isOpen` so
	// it can be called from effects with simple dependency arrays.
	const loadParcelasFromServer = async (docId?: string | number) => {
		setParcelasApi([]);
		if (!docId) return;
		try {
			const resp: any = await documentoService.getById(`${docId}?include=parcelas`);
			const doc = resp && (resp.data || resp) ? (resp.data || resp) : null;
			let parcelasList: any[] = [];
			if (doc) {
				if (Array.isArray(doc.parcelas)) parcelasList = doc.parcelas;
				else if (doc.parcelas && Array.isArray(doc.parcelas.data)) parcelasList = doc.parcelas.data;
			}
			setParcelasApi(parcelasList);
		} catch (err) {
			console.warn('Falha ao carregar parcelas:', err);
		}
	};

	// Este useEffect não é mais necessário, pois os dados vêm com include
	// useEffect(() => {
	// 	if (!isOpen || !docId) {
	// 		setParcelasApi([]);
	// 		setRepassesApi([]);
	// 		setSinistrosApi([]);
	// 		return;
	// 	}
	// 	loadParcelasFromServer(docId);
	// 	loadRepassesFromServer(docId);
	// }, [isOpen, docId]);

	const fetchCatalog = async (path: string) => {
		try {
			const data = await apiClient.request<any>(path);
			// assume API returns array or { data: [...] }
			let result: any[] = [];
			if (Array.isArray(data)) result = data;
			else if (data && Array.isArray(data.data)) result = data.data;
			
			// Ordenar se for seguradoras ou produtos
			if (path.includes('seguradoras') || path.includes('produtos')) {
				result = result.sort((a, b) => (a.nome || '').localeCompare(b.nome || ''));
			}
			
			return result;
		} catch (e) {
			return [];
		}
	};

	// helper to begin editing a parcela row and initialize draft
	const startEditingParcela = (p: Parcela) => {
		setEditingParcelaId(p.id);
		setParcelaDraft({ dataVencimento: p.dataVencimento, dataPagamento: p.dataPagamento, pago: !!p.pago, observacoes: p.observacoes });
	};

	// helper to begin editing a repasse row and initialize draft
	const startEditingRepasse = (r: any) => {
		setEditingRepasseId(r.id);
		setRepasseDraft({
			numeroDocumento: r.numeroDocumento,
			dataPagamento: r.dataPagamento,
			repassePago: r.repassePago,
			informacao: r.informacao,
			documentoOrigem: r.documentoOrigem
		});
	};

	// helper to delete a repasse
	const handleDeleteRepasse = async (r: any) => {
		setRepasseToDelete(r);
		setShowDeleteRepasseConfirm(true);
	};

	const confirmDeleteRepasse = async () => {
		if (!repasseToDelete) return;
		try {
			await repasseService.remove(String(repasseToDelete.id));
			setRepassesApi(prev => prev.filter(x => x.id !== repasseToDelete.id));
			notifier.show({ message: 'Repasse excluído', severity: 'success' });
		} catch (err: any) {
			notifier.show({ message: err?.message || 'Erro ao excluir repasse', severity: 'error' });
		} finally {
			setShowDeleteRepasseConfirm(false);
			setRepasseToDelete(null);
		}
	};

	// Carrega os repasses do servidor para o documento (DEPRECATED - usar reloadDocumentWithRelations)
	// const loadRepassesFromServer = async (docId?: string | number) => {
	// 	if (!docId) return;
	// 	try {
	// 		const response = await repasseService.list({ documentoId: docId });
	// 		setRepassesApi(response || []);
	// 	} catch (err) {
	// 		console.error('Erro ao carregar repasses:', err);
	// 		setRepassesApi([]);
	// 	}
	// };

	// Recarrega o documento completo com todos os relacionamentos
	const reloadDocumentWithRelations = async (docId?: string | number) => {
		if (!docId) return;
		try {
			// Usar apiClient com parâmetros include
			const response = await apiClient.request<any>(`/documentos/${docId}?include=parcelas,repasses,sinistros`);
			const documento = response.data || response;
			
			// Atualizar todos os dados relacionados
			if (documento.parcelas && Array.isArray(documento.parcelas)) {
				setParcelasApi(documento.parcelas);
			}
			if (documento.repasses && Array.isArray(documento.repasses)) {
				setRepassesApi(documento.repasses);
			}
			if (documento.sinistros && Array.isArray(documento.sinistros)) {
				setSinistrosApi(documento.sinistros);
			}
			
			// Atualizar também os dados do formulário se necessário
			setFormFromSource(documento);
		} catch (err) {
			console.error('Erro ao recarregar documento:', err);
		}
	};

	// Gera N parcelas automaticamente seguindo as regras e envia payload em lote:
	// O endpoint espera pelo menos: { documentoId, numeroDocumento, parcelas: [{ numeroParcela, dataVencimento, pago, observacoes, ... }, ...] }
	const criarParcelasAutomaticas = async (quantidade: number) => {
		if (!apolice?.id) {
			notifier.show({ message: 'Salve o documento primeiro antes de criar parcelas', severity: 'warning' });
			return;
		}
		if (!quantidade || quantidade <= 0) {
			notifier.show({ message: 'Quantidade de parcelas inválida', severity: 'warning' });
			return;
		}

		try {
			const dataEmissao = apolice.inicioVigencia ? new Date(apolice.inicioVigencia) : new Date();
			const parcelasPayload: any[] = [];

			for (let i = 0; i < quantidade; i++) {
				let dataVencimento: Date;

				if (i === 0) {
					dataVencimento = new Date(dataEmissao.getTime());
					dataVencimento.setDate(dataVencimento.getDate() + 7);
				} else if (i === 1) {
					dataVencimento = new Date(dataEmissao.getTime());
					dataVencimento.setDate(dataVencimento.getDate() + 30);
				} else {
					const segundaParcela = new Date(dataEmissao.getTime());
					segundaParcela.setDate(segundaParcela.getDate() + 30);
					dataVencimento = new Date(segundaParcela.getTime());
					dataVencimento.setMonth(dataVencimento.getMonth() + (i - 1));
					const diaOriginal = segundaParcela.getDate();
					const diasNoMes = new Date(dataVencimento.getFullYear(), dataVencimento.getMonth() + 1, 0).getDate();
					dataVencimento.setDate(Math.min(diaOriginal, diasNoMes));
				}

				// valorParcela intentionally omitted unless explicitly required by user

				parcelasPayload.push({
					numeroParcela: i + 1,
					dataVencimento: dataVencimento.toISOString(),
					pago: false,
					observacoes: `${i + 1}ª parcela importada`,
					agendado: false
				});
			}

			const body = {
				documentoId: apolice.id,
				numeroDocumento: formData.proposta || (apolice as any)?.codigoProposta || (apolice as any)?.numeroDocumento || '',
				parcelas: parcelasPayload
			};

			// Envia em lote conforme exemplo esperado pelo backend
			await parcelaService.create(body as any);

			// Recarregar documento com relações para refletir as novas parcelas
			await reloadDocumentWithRelations(apolice.id);

			notifier.show({ message: `${quantidade} parcelas criadas com sucesso!`, severity: 'success' });
			setShowNovaParcelaModal(false);
		} catch (err: any) {
			notifier.show({ message: err?.message || 'Erro ao criar parcelas', severity: 'error' });
		}
	};



	// Funções para modal de novo sinistro
	const handleOpenNovoSinistroModal = () => {
		if (!apolice?.id) {
			notifier.show({ message: 'Salve o documento primeiro para adicionar sinistros', severity: 'warning' });
			return;
		}
		
		// Criar um objeto sinistro vazio com dados pré-preenchidos do documento
		const newSinistro = {
			numeroDocumento: formData.proposta || '',
			apolice: formData.apolice || '',
			endosso: formData.endosso || '',
			cpf_cnpj: formData.cpfCnpj || '',
			nome: formData.nome || '',
			data: new Date().toISOString().slice(0, 10),
			informacoes: '',
			tipo: '',
			valor_franquia: '',
			numero_sinistro: '',
			documentoId: apolice?.id,
			oficina: false,
			abertura: false,
			documentos: false,
			vistoria_sinistro: false,
			analise: false,
			aprovacao: false,
			pagamento: false,
			concluido: false
		};
		
		setNovoSinistroDraft(newSinistro);
		setShowNovoSinistroModal(true);
	};

	const handleCloseNovoSinistroModal = () => {
		setShowNovoSinistroModal(false);
		setNovoSinistroDraft({
			numeroDocumento: '',
			apolice: '',
			endosso: '',
			cpf_cnpj: '',
			nome: '',
			data: '',
			informacoes: '',
			tipo: '',
			valor_franquia: '',
			numero_sinistro: '',
			documentoId: '',
			oficina: false,
			abertura: false,
			documentos: false,
			vistoria_sinistro: false,
			analise: false,
			aprovacao: false,
			pagamento: false,
			concluido: false
		});
	};

	const handleSaveNovoSinistro = async (sinistroData: any) => {
		try {
			const payload = {
				numeroDocumento: sinistroData.numeroDocumento,
				// proposta: removido conforme solicitado
				apolice: sinistroData.apolice || undefined,
				endosso: sinistroData.endosso || undefined,
				cpf_cnpj: sinistroData.cpf_cnpj || undefined,
				data: sinistroData.data ? new Date(sinistroData.data).toISOString() : undefined,
				informacoes: sinistroData.informacoes || undefined,
				tipo: sinistroData.tipo || undefined,
				valor_franquia: sinistroData.valor_franquia ? parseFloat(sinistroData.valor_franquia.toString()) : undefined,
				numero_sinistro: sinistroData.numero_sinistro || undefined,
				documentoId: apolice?.id,
				oficina: sinistroData.oficina,
				abertura: sinistroData.abertura,
				documentos: sinistroData.documentos,
				vistoria_sinistro: sinistroData.vistoria_sinistro,
				analise: sinistroData.analise,
				aprovacao: sinistroData.aprovacao,
				pagamento: sinistroData.pagamento,
				concluido: sinistroData.concluido
			};

			await sinistroService.create(payload);
			
			// Recarregar sinistros do documento
			if (apolice?.id) {
				await reloadDocumentWithRelations(apolice.id);
			}
			
			notifier.show({ message: 'Sinistro criado com sucesso!', severity: 'success' });
			handleCloseNovoSinistroModal();
		} catch (err: any) {
			notifier.show({ message: err?.message || 'Erro ao criar sinistro', severity: 'error' });
		}
	};

	// Funções para modal de edição de sinistro
	const handleOpenEditSinistroModal = (sinistro: any) => {
		setEditSinistroDraft({
			...sinistro,
			proposta: sinistro.numeroDocumento || '',
			nome: formData.nome || apolice?.clientes?.[0]?.nome || '',
			data: sinistro.data ? new Date(sinistro.data).toISOString().split('T')[0] : ''
		});
		setShowEditSinistroModal(true);
	};

	const handleCloseEditSinistroModal = () => {
		setShowEditSinistroModal(false);
		setEditSinistroDraft(null);
	};

	const handleSaveEditSinistro = async (sinistroData: any) => {
		try {
			const { proposta, nome, id, ...restFormData } = sinistroData;
			
			const payload = {
				...restFormData,
				numeroDocumento: sinistroData.numeroDocumento,
				data: sinistroData.data ? new Date(sinistroData.data).toISOString() : undefined,
				valor_franquia: sinistroData.valor_franquia ? parseFloat(sinistroData.valor_franquia.toString()) : undefined,
				documentoId: apolice?.id
			};

			await sinistroService.update(sinistroData.id, payload);
			
			// Recarregar sinistros do documento
			if (apolice?.id) {
				await reloadDocumentWithRelations(apolice.id);
			}
			
			notifier.show({ message: 'Sinistro atualizado com sucesso!', severity: 'success' });
			handleCloseEditSinistroModal();
		} catch (err: any) {
			notifier.show({ message: err?.message || 'Erro ao atualizar sinistro', severity: 'error' });
		}
	};

// Try to get a server-generated timestamp so we use server timezone/clock.
// Tries a small list of candidate endpoints and returns the ISO timestamp string
// (or undefined on failure). This lets the backend be the source-of-truth for
// timestamps when available.
const getServerTimestamp = async (): Promise<string | undefined> => {
	const candidates = ['/time', '/server-time', '/timestamp', '/utils/time'];
 	for (const ep of candidates) {
 		try {
 			const res: any = await apiClient.request(ep);
 			// Accept several possible shapes: { timestamp: '...' } or just a string
 			if (!res) continue;
 			if (typeof res === 'string') return res;
 			if (res.timestamp) return String(res.timestamp);
 			if (res.data && res.data.timestamp) return String(res.data.timestamp);
 			// some APIs might return { now: '...' }
 			if (res.now) return String(res.now);
 		} catch (e) {
 			// ignore and try next
 		}
 	}
 	return undefined;
};

	// named delete handler for a parcela — improves debug and guarantees consistent behavior
	const handleDeleteParcela = async (p: Parcela) => {
		if (!p || !p.id) return;
		setParcelaToDelete(p);
		setShowDeleteParcelaConfirm(true);
	};

	const confirmDeleteParcela = async () => {
		if (!parcelaToDelete || !parcelaToDelete.id) return;
		try {
			console.debug('Deleting parcela', parcelaToDelete.id);
			setSavingParcelaById((s) => ({ ...(s || {}), [parcelaToDelete.id]: true }));
			await parcelaService.delete(parcelaToDelete.id as any);
			// reload the whole document relations so parcelas/repasses/sinistros are
			// refreshed consistently. Pass the apolice id explicitly.
			if (apolice?.id) await reloadDocumentWithRelations(apolice.id);
			notifier.show({ message: 'Parcela excluída', severity: 'success' });
		} catch (err: any) {
			// attempt to surface useful server error details
			let detail = err?.message || String(err);
			try {
				if (err && err.response && err.response.data) detail = JSON.stringify(err.response.data);
			} catch (e) { /* ignore */ }
			console.error('Erro ao excluir parcela', parcelaToDelete.id, err);
			notifier.show({ message: detail || 'Erro ao excluir parcela', severity: 'error' });
		} finally {
			setSavingParcelaById((s) => {
				const copy = { ...(s || {}) };
				delete copy[parcelaToDelete.id as any];
				return copy;
			});
			setShowDeleteParcelaConfirm(false);
			setParcelaToDelete(null);
		}
	};

	// Toggle handler for parcela 'pago' checkbox. When marking paid, set a
	// payment date (attempt server timestamp then local). When unchecking (mark
	// as aberta), prompt the user to confirm; on confirm clear the date in the
	// draft. We intentionally don't change state immediately when opening the
	// confirmation so the checkbox remains controlled by the existing draft.
	const handleToggleParcelaPago = async (p: Parcela, checked: boolean) => {
		// prefer draft value if present
		const existing = parcelaDraft?.dataPagamento ?? p.dataPagamento;
		if (!checked) {
			// open confirmation to mark parcela as aberta (not paid)
			setParcelaToToggleOpen(p);
			setShowConfirmAbrirParcela(true);
			return;
		}

		// If checked and we already have a date, preserve it
		if (existing) {
			setParcelaDraft((d) => ({ ...(d || {}), pago: true, dataPagamento: existing }));
			return;
		}

		// Otherwise attempt to fetch server timestamp
		let ts: string | undefined;
		try {
			ts = await getServerTimestamp();
		} catch (e) {
			ts = undefined;
		}
		if (!ts) ts = toSaoPauloTimestamp(new Date());
		setParcelaDraft((d) => ({ ...(d || {}), pago: true, dataPagamento: ts }));
	};

	// helper: fetch all pages from a service.list that returns PaginatedResponse
	const fetchAllFromService = async (service: any, params: any = { limit: 100 }) => {
		const items: any[] = [];
		let page = 1;
		let totalPages = 1;
		try {
			while (page <= totalPages) {
				const res = await service.list({ ...params, page, limit: params.limit });
				const data = (res && (res as any).data) ? (res as any).data : [];
				const pagination = (res && (res as any).pagination) ? (res as any).pagination : null;
				items.push(...(Array.isArray(data) ? data : []));
				if (pagination && typeof pagination.totalPages === 'number') {
					totalPages = pagination.totalPages;
					page++;
				} else {
					// no pagination info -> break after first
					break;
				}
			}
			return items;
		} catch (e) {
			return items;
		}
	};

	const handleInputChange = (field: keyof FormData, value: any) => {
		setFormData((prev) => ({ ...prev, [field]: value }));
	};

	const handleCurrencyChange = (field: keyof FormData, value: string) => {
		// Armazena o valor limpo (sem formatação) no formData
		const cleanValue = parseCurrency(value);
		setFormData((prev) => ({ ...prev, [field]: cleanValue }));
	};

	const getCurrencyDisplayValue = (field: keyof FormData): string => {
		const value = formData[field] as string;
		if (!value || value === '0' || value === '') return '';
		
		// Se já está formatado como moeda, retorna como está
		if (value.includes('R$')) return value;
		
		// Senão, formatar como número
		const numValue = parseFloat(value);
		if (isNaN(numValue)) return '';
		
		return numValue.toLocaleString('pt-BR', {
			style: 'currency',
			currency: 'BRL',
			minimumFractionDigits: 2
		});
	};

	const handleCheckboxChange = (field: keyof FormData) => {
		setFormData((prev) => ({ ...prev, [field]: !prev[field] }));
	};

	// local parcela change handler removed: parcelas are managed via server-backed `parcelasApi` and inline edit state

	const handleSubmit = (e: React.FormEvent) => {
		e.preventDefault();

		const apoliceData: ApoliceData = {
			id: apolice?.id,
			numeroDocumento: formData.proposta,
			numeroApolice: formData.apolice,
			tipoDocumento: 'Apólice',
			dataEmissao: toSaoPauloTimestamp(formData.dataProsposta) || undefined,
			inicioVigencia: toSaoPauloTimestamp(formData.inicioVigencia) || undefined,
			fimVigencia: toSaoPauloTimestamp(formData.terminoVigencia) || undefined,
			premioLiquido: formData.premioLiquido ? parseFloat(formData.premioLiquido.replace(/,/g, '.')) : undefined,
			comissao: formData.comissaoValor ? parseFloat(formData.comissaoValor.replace(/,/g, '.')) : undefined,
			quantidadeParcelas: formData.parcelas ? parseInt(formData.parcelas) : undefined,
			cliente: { nome: formData.nome, cpfCnpj: formData.cpfCnpj },
				seguradora: (typeof formData.seguradora === 'number' || String(formData.seguradora).match(/^\d+$/)) ? { id: formData.seguradora } : { nome: formData.seguradora },
				produto: (typeof formData.produto === 'number' || String(formData.produto).match(/^\d+$/)) ? { id: formData.produto } : { nome: formData.produto },
			status: { nome: formData.situacao },
		} as ApoliceData;

		// map parcelasDetalhes -> parcelas payload expected by backend
		(apoliceData as any).parcelas = formData.parcelasDetalhes.map((p) => ({
			vencimento: toSaoPauloTimestamp(p.data || p.vencer) || null,
			proposta: p.proposta || formData.proposta,
			apolice: p.apolice || formData.apolice,
			sequencia: p.sequencia || undefined,
			pago: !!p.pago,
		}));

		onSave(apoliceData);
	};

	const [saving, setSaving] = useState(false);

	const notifier = useNotifier();

	const saveToApi = async (closeAfter = false) => {
		setSaving(true);
		try {
			console.log('🔍 Iniciando saveToApi - closeAfter:', closeAfter);
			console.log('📝 FormData atual:', formData);
			// Build payload compatible with backend contract (prefer ids, normalize cpf/cnpj)
			const onlyDigits = (v?: string) => (v ? String(v).replace(/\D+/g, '') : undefined);
			const looksLikeUuid = (v?: any) => typeof v === 'string' && /^[0-9a-fA-F\-]{36}$/.test(v);

			// helper to resolve an id from a catalog list by id or name
			const resolveIdFromCatalog = (val: any, list: any[] | undefined) => {
				if (!val) return undefined;
				if (looksLikeUuid(val)) return val;
				if (!list || !Array.isArray(list)) return undefined;
				const vstr = String(val).toLowerCase();
				const found = list.find((x: any) => String(x.id || '').toLowerCase() === vstr || String(x.nome || x.name || '').toLowerCase() === vstr);
				return found ? found.id : undefined;
			};

			const payload: any = {
				tipoDocumento: 'Apólice',
				numeroDocumento: formData.apolice || formData.proposta || undefined,
				dataEmissao: toSaoPauloTimestamp(formData.dataProsposta) || undefined,
				dataValidade: toSaoPauloTimestamp(formData.terminoVigencia) || undefined,
				fimVigencia: toSaoPauloTimestamp(formData.terminoVigencia) || undefined,
				dataEntrada: toSaoPauloTimestamp(formData.dataEntrada) || undefined,
				inicioVigencia: toSaoPauloTimestamp(formData.inicioVigencia) || undefined,
				dataExtracao: toSaoPauloTimestamp(new Date()),

				// numeric fields (if present in form)
				premioLiquido: formData.premioLiquido ? parseFloat(String(formData.premioLiquido).replace(/,/g, '.')) : undefined,
				comissao: formData.comissaoValor ? parseFloat(String(formData.comissaoValor).replace(/,/g, '.')) : undefined,
				percentualComissao: formData.comissaoPercent ? parseFloat(String(formData.comissaoPercent).replace(/,/g, '.')) : undefined,
				quantidadeParcelas: formData.parcelas ? parseInt(String(formData.parcelas), 10) : undefined,

				// prefer ids when possible; fall back to sending nested object with nome
				empresaId: (formData as any).empresaId || (looksLikeUuid(formData.empresa) ? formData.empresa : undefined),
				clienteId: (formData as any).clienteId || undefined,
				cpfCnpjCliente: (formData as any).clienteId ? undefined : onlyDigits(formData.cpfCnpj) || undefined,

				// Prefer enviar IDs para relações: pagamento, status e situacao
				pagamentoId: resolveIdFromCatalog((formData as any).pagamentoId || (formData as any).pagamento || undefined, catalogs.pagamentos),
				statusId: resolveIdFromCatalog((formData as any).status || undefined, catalogs.status),
				situacaoId: resolveIdFromCatalog((formData as any).situacao || undefined, catalogs.situacoes),
				
				// Seguradora e Produto - enviar APENAS o ID quando for UUID
				seguradoraId: looksLikeUuid(formData.seguradora) ? formData.seguradora : undefined,
				produtoId: looksLikeUuid(formData.produto) ? formData.produto : undefined,

				// boolean flags (use canonical fields only)
				propostaEnviada: !!formData.propostaEnviada,
				boletoEnviado: !!formData.boletoEnviado,
				apoliceEnviada: !!formData.apoliceEnviada,
				kitEnviado: !!formData.kitEnviado,
				renovacaoEnviada: !!formData.renovacaoEnviada,
				renovacaoAprovada: !!formData.renovacaoAprovada,
				marcarEnvioApolice: !!formData.marcarEnvioApolice,
				marcarEnvioProposta: !!formData.marcarEnvioProposta,
				exigeAnaliseTecnica: !!formData.exigeAnaliseTecnica,
				exigeVistoria: !!formData.exigeVistoria,

				// newly requested boolean flags (include both naming variants when present)
				renovacaoMensagem: !!(formData as any).renovacaoMensagem || !!(formData as any).mensagemRenovacao,
				boasvindasMensagem: !!(formData as any).boasvindasMensagem || !!(formData as any).mensagemBoasVindas,
				propostaNaPasta: !!formData.propostaNaPasta,
				propostaZapsign: !!formData.propostaZapsign,

				// textual fields
				observacoes: formData.informacoesComplementares || undefined,
				dataCancelamento: formData.dataCancel ? toSaoPauloTimestamp(formData.dataCancel) : undefined,
				motivoCancelamento: formData.motivo || undefined,

				// aliases/backward-compatible fields
				numeroApolice: formData.apolice || undefined,
				numeroEndosso: formData.endosso || undefined,
			};

			console.log('📦 Payload construído:', payload);

				// helper to remove empty strings / null / undefined and drop empty nested objects/arrays
				const cleanObject = (input: any): any => {
					if (input === null || typeof input === 'undefined') return undefined;
					if (typeof input !== 'object') return input;
					if (Array.isArray(input)) {
						const arr = input.map((i) => cleanObject(i)).filter((i) => typeof i !== 'undefined');
						return arr.length ? arr : undefined;
					}
					const out: any = {};
					Object.entries(input).forEach(([k, v]) => {
						const cleaned = cleanObject(v);
						if (typeof cleaned === 'string' && cleaned.trim() === '') return;
						if (typeof cleaned === 'undefined' || cleaned === null) return;
						if (typeof cleaned === 'object' && (Object.keys(cleaned).length === 0 || (Array.isArray(cleaned) && cleaned.length === 0))) return;
						out[k] = cleaned;
					});
					return Object.keys(out).length ? out : undefined;
				};

				let saved: any = null;
				if (apolice && apolice.id) {
					console.log('🔄 Atualizando documento existente, ID:', apolice.id);
					// For updates, send only minimal required + changed fields (NOT numeroDocumento - read-only)
					const minimal: any = {
						cpfCnpjCliente: payload.cpfCnpjCliente,
						dataEmissao: payload.dataEmissao,
						fimVigencia: payload.fimVigencia,
						dataExtracao: payload.dataExtracao,
						tipoDocumento: payload.tipoDocumento,
					};
						// compute diffs vs originalSource (skip numeroDocumento - read-only field)
					if (originalSource) {
						Object.keys(payload).forEach((k) => {
							if (k === 'numeroDocumento') return; // Skip - read-only field
							const newVal = payload[k];
							const oldVal = originalSource[k];
							// compare as strings for simplicity
							if (typeof newVal !== 'undefined' && String(newVal) !== String(oldVal)) {
								minimal[k] = newVal;
							}
						});
					}

						// Always include checkbox/flag fields AND important relation fields so toggles/relations are sent on update
						const ALWAYS_SEND_FLAGS = [
							'propostaEnviada', 'boletoEnviado', 'apoliceEnviada', 'kitEnviado', 'renovacaoEnviada', 'renovacaoAprovada',
							'marcarEnvioApolice', 'marcarEnvioProposta', 'exigeAnaliseTecnica', 'exigeVistoria',
							'renovacaoMensagem', 'boasvindasMensagem', 'propostaNaPasta', 'propostaZapsign'
						];
						ALWAYS_SEND_FLAGS.forEach((f) => {
							if (typeof payload[f] !== 'undefined') minimal[f] = payload[f];
						});
						
						// Always send relation IDs (important relational fields)
						const ALWAYS_SEND_RELATIONS = ['statusId', 'situacaoId', 'seguradoraId', 'produtoId', 'pagamentoId'];
						ALWAYS_SEND_RELATIONS.forEach((f) => {
							if (typeof payload[f] !== 'undefined') minimal[f] = payload[f];
						});
					// clean minimal payload to avoid sending empty strings or empty nested objects
					const cleanedMinimal = cleanObject(minimal) || {};
					// ensure we don't overwrite observacoes with empty value from the client
					if ('observacoes' in payload && (payload.observacoes === undefined || (typeof payload.observacoes === 'string' && payload.observacoes.trim() === ''))) {
						delete cleanedMinimal.observacoes;
					}
								console.log('📤 Enviando para UPDATE:', cleanedMinimal);
								console.log('📤 JSON Stringified:', JSON.stringify(cleanedMinimal, null, 2));
								// Defensive: ensure the document exists and obtain canonical id from server
								try {
									const existingResp: any = await documentoService.getById(apolice.id);
									const canonicalId = existingResp && existingResp.data ? existingResp.data.id : (existingResp && (existingResp.id || existingResp)) || apolice.id;
									try {
										// Primary attempt: use service.update
										saved = await documentoService.update(canonicalId, cleanedMinimal);
									} catch (updateErr: any) {
										console.warn('Update failed, attempting fallback strategies', updateErr);
										// If 404, try PUT to the BY_ID path including includes (some backends route differently)
										const status = updateErr?.status || updateErr?.body?.status;
										if (status === 404) {
											try {
												const endpoint = `/documentos/${canonicalId}?include=parcelas,repasses,sinistros`;
												saved = await apiClient.request(endpoint, {
													method: 'PUT',
													body: cleanedMinimal,
												});
											} catch (e2: any) {
												// Try PATCH as a last resort
												try {
													saved = await apiClient.request(`/documentos/${canonicalId}`, {
														method: 'PATCH',
														body: cleanedMinimal,
													});
												} catch (e3: any) {
													console.error('All update fallbacks failed', e3);
													throw e3;
												}
											}
										} else {
											throw updateErr;
										}
									}
								} catch (e: any) {
									// If the GET fails with 404, surface a clearer message and abort update
									const errMsg = e?.message || 'Registro não encontrado ao validar documento antes de atualizar.';
									console.error('Falha ao validar documento antes do UPDATE:', e);
									throw new Error(errMsg);
								}
				} else {
					console.log('🆕 Criando novo documento');
					// create: send minimal required fields only
					const createPayload: any = {
						numeroDocumento: payload.numeroDocumento,
						cpfCnpjCliente: payload.cpfCnpjCliente,
						dataEmissao: payload.dataEmissao,
						fimVigencia: payload.fimVigencia,
						dataExtracao: payload.dataExtracao,
						tipoDocumento: payload.tipoDocumento,
						// include checkbox flags on create as well (canonical names)
						exigeAnaliseTecnica: payload.exigeAnaliseTecnica,
						exigeVistoria: payload.exigeVistoria,
						marcarEnvioProposta: payload.marcarEnvioProposta,
						boletoEnviado: payload.boletoEnviado,
						apoliceEnviada: payload.apoliceEnviada,
						propostaEnviada: payload.propostaEnviada,
						// newly requested flags on create
						renovacaoMensagem: payload.renovacaoMensagem,
						boasvindasMensagem: payload.boasvindasMensagem || payload.mensagemBoasVindas,
						propostaNaPasta: payload.propostaNaPasta,
						propostaZapsign: payload.propostaZapsign,
					};
					const cleanedCreate = cleanObject(createPayload) || {};
					console.log('📤 Enviando para CREATE:', cleanedCreate);
					saved = await documentoService.create(cleanedCreate);
				}

			// documentoService extends BaseService which returns ApiResponse<T>
			const result = (saved && (saved.data || saved)) || saved;
			// show success notification (use server returned message if present)
			let okMessage = 'Documento salvo com sucesso.';
			if (result && (result as any).message) okMessage = String((result as any).message);
			notifier.show({ message: okMessage, severity: 'success' });

			// Merge returned data with originalSource to avoid clearing fields
			// that the API may not return. Prefer server values when present,
			// but keep existing originalSource values for keys missing from result.
			const returnedObj = (result as any) || {};
			const merged = originalSource ? { ...(originalSource as any), ...returnedObj } : returnedObj;

			// refresh the form with the merged data when not closing
			if (!closeAfter) {
				setOriginalSource(merged || null);
				setFormFromSource(merged || {});
				setOriginalSnapshot(normalizeForSnapshot(merged || {}));
				// Notify parent to refresh without closing the modal
				if (typeof onUpdated === 'function') {
					onUpdated(merged as ApoliceData);
				}
			} else {
				// when closing after save, call onSave so parent can refresh list, then close
				onSave(merged as ApoliceData);
				onClose();
			}
		} catch (err: any) {
			console.error('Erro ao salvar documento:', err);
			// try to extract backend message
						let message = 'Erro ao salvar documento.';
						if (err && err.response && err.response.data && err.response.data.message) message = String(err.response.data.message);
						else if (err && err.message) message = String(err.message);

						// Translate common DB unique-constraint errors to friendlier text
						if (message.includes('Registro duplicado') || message.toLowerCase().includes('duplicate')) {
							message = 'Já existe um documento com esse número. Tente localizar ou editar o documento existente.';
						}
			notifier.show({ message, severity: 'error' });
			// Do not re-throw here to avoid unhandled promise rejection when callers
			// call saveToApi() without awaiting. Caller (parent) will receive onSave only on success.
			return;
		} finally {
			setSaving(false);
		}
	};

	const isDirty = () => {
		try {
			if (!originalSnapshot) return false;
			// parse original snapshot if possible
			let originalObj: any = null;
			try {
				originalObj = JSON.parse(originalSnapshot);
			} catch (e) {
				originalObj = null;
			}

			// build current normalized object from formData
			const current: any = {
				status: (formData.status || '').trim(),
				proposta: (formData.proposta || '').trim(),
				apolice: (formData.apolice || '').trim(),
				dataProsposta: toInputDate(formData.dataProsposta) || '',
				dataEntrada: toInputDate(formData.dataEntrada) || '',
				inicioVigencia: toInputDate(formData.inicioVigencia) || '',
				terminoVigencia: toInputDate(formData.terminoVigencia) || '',
				endosso: (formData.endosso || '').trim(),
				seguradora: String(formData.seguradora || '').trim(),
				produto: String(formData.produto || '').trim(),
				premioLiquido: (formData.premioLiquido || '').toString().trim(),
				comissaoValor: (formData.comissaoValor || '').toString().trim(),
				comissaoPercent: (formData.comissaoPercent || '').toString().trim(),
				cpfCnpj: (formData.cpfCnpj || '').trim(),
				nome: (formData.nome || '').trim(),
				email: (formData.email || '').trim(),
				telefone: (formData.telefone || '').trim(),
				situacao: (formData.situacao || '').trim(),
				informacoesComplementares: (formData.informacoesComplementares || '').trim(),
				parcelas: (formData.parcelas || '').toString().trim(),
			};

			const normalizeString = (v: any) => (v === null || typeof v === 'undefined' ? '' : String(v).replace(/\s+/g, ' ').trim());
			const normalizeNumber = (v: any) => {
				if (v === null || typeof v === 'undefined' || v === '') return null;
				const n = Number(String(v).replace(/,/g, '.'));
				return Number.isFinite(n) ? n : null;
			};

			// compare a single key with type-aware normalization
			const equalKey = (k: string) => {
				const origVal = originalObj ? originalObj[k] : undefined;
				const curVal = current[k];
				if (['premioLiquido', 'comissaoValor', 'comissaoPercent'].includes(k)) {
					return normalizeNumber(origVal) === normalizeNumber(curVal);
				}
				if (k.toLowerCase().includes('data') || k === 'inicioVigencia' || k === 'terminoVigencia') {
					// compare normalized date-only strings
					const o = toInputDate(origVal) || '';
					const c = toInputDate(curVal) || '';
					return o === c;
				}
				// fallback: string compare with whitespace collapse
				return normalizeString(origVal) === normalizeString(curVal);
			};

			for (const k of Object.keys(current)) {
				if (!equalKey(k)) return true; // there is a difference -> dirty
			}
			return false;
		} catch (e) {
			// if anything goes wrong, be permissive (don't block navigation)
			return false;
		}
	};

	const [confirmOpen, setConfirmOpen] = useState(false);

	const handleNext = async () => {
		if (!onNextRequested) return;
		if (isDirty()) {
			setConfirmOpen(true);
			return;
		}
		// not dirty: go directly
		const next = await onNextRequested(apolice?.id);
		if (next) {
			setOriginalSource(next);
			setFormFromSource(next);
			setOriginalSnapshot(normalizeForSnapshot(next || {}));
		} else {
			// reached the end: close the modal so user can reopen/edit manually
			onClose();
		}
	};

	const onConfirmSave = async () => {
		setConfirmOpen(false);
		try {
			await saveToApi(false);
			const next = await onNextRequested?.(apolice?.id);
			if (next) {
				setOriginalSource(next);
				setFormFromSource(next);
				setOriginalSnapshot(normalizeForSnapshot(next || {}));
			} else {
				// reached the end: close the modal so user can reopen/edit manually
				onClose();
			}
		} catch (err) {
			// saveToApi already notifies on error
		}
	};

	const onConfirmDiscard = async () => {
		setConfirmOpen(false);
		const next = await onNextRequested?.(apolice?.id);
		if (next) {
			setOriginalSource(next);
			setFormFromSource(next);
			setOriginalSnapshot(normalizeForSnapshot(next || {}));
		} else {
			// reached the end: close the modal so user can reopen/edit manually
			onClose();
		}
	};

	const onConfirmCancel = () => {
		setConfirmOpen(false);
	};

	if (!isOpen) return null;

	return (
		<>
			<div className="fixed inset-0 flex items-center justify-center z-50 backdrop-blur-sm" style={{ backgroundColor: 'rgba(61, 43, 86, 0.5)' }}>
				<div className="bg-white w-[95vw] max-w-[1400px] max-h-[90vh] rounded-2xl shadow-2xl overflow-hidden">
					{/* Header com dados principais */}
					<div className="bg-white px-4 py-3" style={{ borderBottom: '2px solid #C3B4DE' }}>
					<div className="flex items-center justify-between">
						<div className="flex items-center space-x-3">
							<div className="w-6 h-6 rounded-md flex items-center justify-center" style={{ backgroundColor: '#F4EFFA' }}>
								<svg className="w-4 h-4 text-gray-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
									<path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
								</svg>
							</div>
							<div>
								<h3 className="text-sm font-medium text-gray-900">{isEditing ? 'Editar Apólice' : 'Nova Apólice'}</h3>
								<div className="flex items-center space-x-4 text-xs text-gray-500 mt-0.5">
									{formData.apolice && (
										<span className="flex items-center gap-1">
											<svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
												<path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 21h10a2 2 0 002-2V9.414a1 1 0 00-.293-.707l-5.414-5.414A1 1 0 0012.586 3H7a2 2 0 00-2 2v14a2 2 0 002 2z" />
											</svg>
											{formData.apolice}
										</span>
									)}
									{formData.empresa && (
										<span className="flex items-center gap-1">
											<svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
												<path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 7h18M3 11h18M3 15h18M3 19h18" />
											</svg>
											{formData.empresa}
										</span>
									)}
									{formData.nome && (
										<span className="flex items-center gap-1">
											<svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
												<path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
											</svg>
											{formData.nome}
										</span>
									)}
									{formData.seguradora && (
										<span className="flex items-center gap-1">
											<svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
												<path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
											</svg>
											{
												// if the field is an object with a name, prefer that
												(typeof formData.seguradora === 'object' && formData.seguradora && (formData.seguradora as any).nome)
													? (formData.seguradora as any).nome
													: (() => {
													const value = formData.seguradora;
													const valStr = String(value ?? '');
													const found = catalogs.seguradoras.find((s) => String(s.id) === valStr || String(s.nome) === valStr);
													return found ? found.nome : valStr;
												})()
											}
										</span>
									)}
									{formData.produto && (
										<span className="flex items-center gap-1">
											<svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
												<path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M20 7l-8-4-8 4m16 0l-8 4m8-4v10l-8 4m0-10L4 7m8 4v10M4 7v10l8 4" />
											</svg>
											{
												(typeof formData.produto === 'object' && formData.produto && (formData.produto as any).nome)
													? (formData.produto as any).nome
													: (() => {
													const value = formData.produto;
													const valStr = String(value ?? '');
													const found = catalogs.produtos.find((p) => String(p.id) === valStr || String(p.nome) === valStr);
													return found ? found.nome : valStr;
												})()
											}
										</span>
									)}
								</div>
							</div>
						</div>
						<div className="flex items-center gap-2">
							<button
								type="button"
								disabled={saving}
								onClick={handleNext}
								className="px-3 py-1 text-white rounded text-xs flex items-center gap-2"
								style={{ backgroundColor: '#7A61B2' }}
							>
								<svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
									<path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 7l5 5m0 0l-5 5m5-5H6" />
								</svg>
								Próximo registro
							</button>
							<button 
								onClick={onClose} 
								className="w-6 h-6 rounded-md flex items-center justify-center transition-colors"
								style={{ backgroundColor: '#F4EFFA', color: '#7A61B2' }}
							>
								<svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
									<path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
								</svg>
							</button>
						</div>
					</div>
				</div>

				{/* Conteúdo com scroll */}
				<div className="overflow-y-auto max-h-[calc(90vh-60px)]" style={{ backgroundColor: '#F4EFFA' }}>
					<div className="p-6">

						<form onSubmit={handleSubmit} className="text-xs space-y-4">
							{/* Barra de controles superior */}
							<div className="bg-white rounded-xl shadow-sm p-4 flex justify-between items-center" style={{ border: '1px solid #C3B4DE' }}>
								<div className="flex flex-wrap gap-4">
								{/* Render only canonical whitelist checkboxes */}
								<label className="flex items-center gap-2 text-xs font-medium text-gray-700 hover:text-gray-900 transition-colors">
									<input type="checkbox" checked={!!formData.propostaEnviada} onChange={() => handleCheckboxChange('propostaEnviada' as any)} className="w-4 h-4 text-indigo-600 rounded border-gray-300 focus:ring-purple-500" />
									Proposta enviada
								</label>
								<label className="flex items-center gap-2 text-xs font-medium text-gray-700 hover:text-gray-900 transition-colors">
									<input type="checkbox" checked={!!formData.boletoEnviado} onChange={() => handleCheckboxChange('boletoEnviado' as any)} className="w-4 h-4 text-indigo-600 rounded border-gray-300 focus:ring-purple-500" />
									Boleto enviado
								</label>
								<label className="flex items-center gap-2 text-xs font-medium text-gray-700 hover:text-gray-900 transition-colors">
									<input type="checkbox" checked={!!formData.apoliceEnviada} onChange={() => handleCheckboxChange('apoliceEnviada' as any)} className="w-4 h-4 text-indigo-600 rounded border-gray-300 focus:ring-purple-500" />
									Apólice enviada
								</label>
								<label className="flex items-center gap-2 text-xs font-medium text-gray-700 hover:text-gray-900 transition-colors">
									<input type="checkbox" checked={!!formData.kitEnviado} onChange={() => handleCheckboxChange('kitEnviado' as any)} className="w-4 h-4 text-indigo-600 rounded border-gray-300 focus:ring-purple-500" />
									Autorização de débito
								</label>
								{/* Renovação flags: hidden by default, shown when this modal appears to be for a renewal */}
								{isFromRenovationCard && (
									<>
										<label className="flex items-center gap-2 text-xs font-medium text-gray-700 hover:text-gray-900 transition-colors">
											<input type="checkbox" checked={!!formData.renovacaoEnviada} onChange={() => handleCheckboxChange('renovacaoEnviada' as any)} className="w-4 h-4 text-indigo-600 rounded border-gray-300 focus:ring-purple-500" />
											Renovação enviada
										</label>
										<label className="flex items-center gap-2 text-xs font-medium text-gray-700 hover:text-gray-900 transition-colors">
											<input type="checkbox" checked={!!formData.renovacaoAprovada} onChange={() => handleCheckboxChange('renovacaoAprovada' as any)} className="w-4 h-4 text-indigo-600 rounded border-gray-300 focus:ring-purple-500" />
											Renovação aprovada
										</label>
									</>
								)}
								{/* marcarEnvio checkboxes hidden per request */}
								<label className="flex items-center gap-2 text-xs font-medium text-gray-700 hover:text-gray-900 transition-colors">
									<input type="checkbox" checked={!!formData.exigeAnaliseTecnica} onChange={() => handleCheckboxChange('exigeAnaliseTecnica' as any)} className="w-4 h-4 text-indigo-600 rounded border-gray-300 focus:ring-purple-500" />
									Exige análise técnica
								</label>
								{/* exigeVistoria hidden per request */}
								{/* new message/proposal flags */}
								<label className="flex items-center gap-2 text-xs font-medium text-gray-700 hover:text-gray-900 transition-colors">
									<input type="checkbox" checked={!!formData.renovacaoMensagem} onChange={() => handleCheckboxChange('renovacaoMensagem' as any)} className="w-4 h-4 text-indigo-600 rounded border-gray-300 focus:ring-purple-500" />
									Renovação mensagem
								</label>
								<label className="flex items-center gap-2 text-xs font-medium text-gray-700 hover:text-gray-900 transition-colors">
									<input type="checkbox" checked={!!formData.boasvindasMensagem} onChange={() => handleCheckboxChange('boasvindasMensagem' as any)} className="w-4 h-4 text-indigo-600 rounded border-gray-300 focus:ring-purple-500" />
									Boas-vindas mensagem
								</label>
								<label className="flex items-center gap-2 text-xs font-medium text-gray-700 hover:text-gray-900 transition-colors">
									<input type="checkbox" checked={!!formData.propostaNaPasta} onChange={() => handleCheckboxChange('propostaNaPasta' as any)} className="w-4 h-4 text-indigo-600 rounded border-gray-300 focus:ring-purple-500" />
									Proposta na pasta
								</label>
								<label className="flex items-center gap-2 text-xs font-medium text-gray-700 hover:text-gray-900 transition-colors">
									<input type="checkbox" checked={!!formData.propostaZapsign} onChange={() => handleCheckboxChange('propostaZapsign' as any)} className="w-4 h-4 text-indigo-600 rounded border-gray-300 focus:ring-purple-500" />
									Proposta zapsign
								</label>
							</div>
								<button
									type="button"
									onClick={() => {
										console.debug('ApoliceFormModal: header Agendar clicked, formData:', formData);
										notifier.show({ message: 'Abrindo modal de agendamento...', severity: 'info' });
										// build description from current formData
										const clienteNome = (formData.nome || '').trim();
                                        
										// Get produto name (resolve from catalog if needed)
										const produtoNome = (() => {
											const v = formData.produto;
											if (!v) return '';
											if (typeof v === 'object') return (v as any).nome || '';
											const valStr = String(v || '');
											const found = catalogs.produtos.find((p) => String(p.id) === valStr);
											return found ? found.nome : valStr;
										})();
                                        
										// Get seguradora name (resolve from catalog if needed)
										const seguradoraNome = (() => {
											const v = formData.seguradora;
											if (!v) return '';
											if (typeof v === 'object') return (v as any).nome || '';
											const valStr = String(v || '');
											const found = catalogs.seguradoras.find((s) => String(s.id) === valStr);
											return found ? found.nome : valStr;
										})();
                                        
										const apoliceNum = formData.apolice || '';
										const endosso = formData.endosso || '';
                                        
										const parts: string[] = [];
										if (clienteNome) parts.push(clienteNome);
										if (produtoNome && seguradoraNome) parts.push(`${produtoNome} - ${seguradoraNome}`);
										else if (produtoNome) parts.push(produtoNome);
										else if (seguradoraNome) parts.push(seguradoraNome);
										if (apoliceNum && endosso) parts.push(`Apólice ${apoliceNum} - Endosso ${endosso}`);
										else if (apoliceNum) parts.push(`Apólice ${apoliceNum}`);
										else if (endosso) parts.push(`Endosso ${endosso}`);
                                        
										setInitialDescricaoAgendamento(parts.join('\n') || undefined);
										setOpenAgendamentoModal(true);
									}}
									className="px-4 py-2 text-white rounded-lg text-xs font-medium whitespace-nowrap transition-colors flex items-center gap-2 shadow-sm"
									style={{ backgroundColor: '#7A61B2' }}
								>
									Agendar
								</button>
							</div>

							{/* Seções principais do formulário */}
							<div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
								{/* Seção 1: Informações do Documento */}
								<div className="bg-white rounded-xl shadow-sm p-5" style={{ border: '1px solid #C3B4DE' }}>
									<div className="flex items-center gap-3 mb-4">
										<div className="w-8 h-8 rounded-lg flex items-center justify-center" style={{ backgroundColor: '#F4EFFA' }}>
											<svg className="w-5 h-5 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
												<path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
											</svg>
										</div>
										<h4 className="text-sm font-semibold text-gray-900">Informações do Documento</h4>
									</div>
									<div className="grid grid-cols-2 gap-4">
										{/* Coluna da esquerda */}
										<div className="space-y-4">
											<div>
												<label className="text-xs font-semibold text-gray-700 mb-2 flex items-center gap-1">
													<svg className="w-3 h-3 text-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
														<path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
													</svg>
													Status
												</label>
												{catalogs.status && catalogs.status.length > 0 ? (
													<select value={formData.status} onChange={(e) => handleInputChange('status', e.target.value)} className="w-full px-3 py-2 border rounded-lg text-xs focus:bg-white transition-colors" style={{ backgroundColor: '#F4EFFA', borderColor: '#C3B4DE' }}>
														<option value="">Selecione...</option>
														{catalogs.status.map((s) => (
															<option key={s.id || s.nome} value={s.nome}>{s.nome}</option>
														))}
													</select>
												) : (
													<select value={formData.status} onChange={(e) => handleInputChange('status', e.target.value)} className="w-full px-3 py-2 border rounded-lg text-xs focus:bg-white transition-colors" style={{ backgroundColor: '#F4EFFA', borderColor: '#C3B4DE' }}>
														<option>Proposta</option>
														<option>Apólice</option>
														<option>Cancelado</option>
													</select>
												)}
											</div>
											<div>
												<label className="text-xs font-semibold text-gray-700 mb-2 flex items-center gap-1">
													<svg className="w-3 h-3 text-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
														<path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v10a2 2 0 002 2h8a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" />
													</svg>
													Proposta
												</label>
												<input 
													type="text" 
													value={formData.proposta} 
													readOnly
													className="w-full px-3 py-2 border border-gray-300 rounded-lg text-xs bg-gray-100 cursor-not-allowed transition-colors placeholder-gray-400" 
													placeholder="Número da proposta..." 
												/>
											</div>
									<div>
										<label className="block text-xs font-medium text-gray-700 mb-0.5">Cálculo Col</label>
										<input type="text" value={formData.calculoCol} onChange={(e) => handleInputChange('calculoCol', e.target.value)} className="w-full p-1 border border-gray-300 rounded-md text-xs" />
									</div>
									<div>
										<label className="block text-xs font-medium text-gray-700 mb-0.5">Proposta Cia</label>
										<input type="text" value={formData.propostaCia} onChange={(e) => handleInputChange('propostaCia', e.target.value)} className="w-full p-1 border border-gray-300 rounded-md text-xs" />
									</div>
									<div>
										<label className="block text-xs font-medium text-gray-700 mb-0.5">Apólice</label>
										<input 
											type="text" 
											value={formData.apolice} 
											onChange={(e) => handleInputChange('apolice', e.target.value)} 
											className="w-full p-1 border border-gray-300 rounded-md text-xs"
										/>
									</div>
								</div>

								{/* right column */}
								<div className="space-y-2">
									<div>
										<label className="block text-xs font-medium text-gray-700 mb-0.5">Situação</label>
										{catalogs.situacoes && catalogs.situacoes.length > 0 ? (
											<select value={formData.situacao} onChange={(e) => handleInputChange('situacao', e.target.value)} className="w-full p-1 border border-gray-300 rounded-md text-xs">
												<option value="">--</option>
												{catalogs.situacoes.map((s) => <option key={s.id || s.nome} value={s.nome}>{s.nome}</option>)}
											</select>
										) : (
											<select value={formData.situacao} onChange={(e) => handleInputChange('situacao', e.target.value)} className="w-full p-1 border border-gray-300 rounded-md text-xs">
												<option value="Ativa">Ativa</option>
												<option value="Cancelada">Cancelada</option>
												<option value="Vencida">Vencida</option>
											</select>
										)}
									</div>
									<div>
										<label className="block text-xs font-medium text-gray-700 mb-0.5">Data Proposta</label>
										<input type="date" value={formData.dataProsposta} onChange={(e) => handleInputChange('dataProsposta', e.target.value)} className="w-full p-1 border border-gray-300 rounded-md text-xs" />
									</div>
									<div>
										<label className="block text-xs font-medium text-gray-700 mb-0.5">Data Entrada</label>
										<input type="date" value={formData.dataEntrada} onChange={(e) => handleInputChange('dataEntrada', e.target.value)} className="w-full p-1 border border-gray-300 rounded-md text-xs" />
									</div>
									<div>
										<label className="block text-xs font-medium text-gray-700 mb-0.5">Início Vigência</label>
										<input type="date" value={formData.inicioVigencia} onChange={(e) => handleInputChange('inicioVigencia', e.target.value)} className="w-full p-1 border border-gray-300 rounded-md text-xs" />
									</div>
									<div>
										<label className="block text-xs font-medium text-gray-700 mb-0.5">Término Vigência</label>
										<input type="date" value={formData.terminoVigencia} onChange={(e) => handleInputChange('terminoVigencia', e.target.value)} className="w-full p-1 border border-gray-300 rounded-md text-xs" />
									</div>
									<div>
										<label className="block text-xs font-medium text-gray-700 mb-0.5">Endosso</label>
										<input type="text" value={formData.endosso} onChange={(e) => handleInputChange('endosso', e.target.value)} className="w-full p-1 border border-gray-300 rounded-md text-xs" />
									</div>
								</div>
							</div>
						</div>

						{/* Seguradora, Produto, Prêmio, Comissão */}
						<div className="border rounded p-3 bg-white">
							<h4 className="text-xs font-medium mb-2">Seguradora e Produto</h4>
							<div className="space-y-2">
								<div>
									<label className="block text-xs font-medium text-gray-700 mb-0.5">Seguradora</label>
									{catalogs.seguradoras.length > 0 ? (
										<select value={String(formData.seguradora ?? '')} onChange={(e) => handleInputChange('seguradora', e.target.value === '' ? '' : (isNaN(Number(e.target.value)) ? e.target.value : Number(e.target.value)))} className="w-full p-1 border border-gray-300 rounded-md text-xs">
											<option value="">--</option>
											{catalogs.seguradoras.map((s) => (
												<option key={s.id || s.nome} value={s.id ?? s.nome}>{s.nome}</option>
											))}
										</select>
									) : (
										<input type="text" value={formData.seguradora} onChange={(e) => handleInputChange('seguradora', e.target.value)} className="w-full p-1 border border-gray-300 rounded-md text-xs" />
									)}
								</div>
								<div>
									<label className="block text-xs font-medium text-gray-700 mb-0.5">Produto</label>
									{catalogs.produtos.length > 0 ? (
										<select value={String(formData.produto ?? '')} onChange={(e) => handleInputChange('produto', e.target.value === '' ? '' : (isNaN(Number(e.target.value)) ? e.target.value : Number(e.target.value)))} className="w-full p-1 border border-gray-300 rounded-md text-xs">
											<option value="">--</option>
											{catalogs.produtos.map((p) => (
												<option key={p.id || p.nome} value={p.id ?? p.nome}>{p.nome}</option>
											))}
										</select>
									) : (
										<input type="text" value={formData.produto} onChange={(e) => handleInputChange('produto', e.target.value)} className="w-full p-1 border border-gray-300 rounded-md text-xs" />
									)}
								</div>
								<div className="grid grid-cols-3 gap-2">
									<div>
										<label className="block text-xs font-medium text-gray-700 mb-0.5">Prêmio Líquido</label>
										<input 
											type="text" 
											value={getCurrencyDisplayValue('premioLiquido')} 
											onChange={(e) => handleCurrencyChange('premioLiquido', e.target.value)} 
											className="w-full p-1 border border-gray-300 rounded-md text-xs"
											placeholder="R$ 0,00"
										/>
									</div>
									<div>
										<label className="block text-xs font-medium text-gray-700 mb-0.5">% Comissão</label>
										<input type="text" value={formData.comissaoPercent} onChange={(e) => handleInputChange('comissaoPercent', e.target.value)} className="w-full p-1 border border-gray-300 rounded-md text-xs" />
									</div>
									<div>
										<label className="block text-xs font-medium text-gray-700 mb-0.5">Valor Comissão</label>
										<input 
											type="text" 
											value={getCurrencyDisplayValue('comissaoValor')} 
											onChange={(e) => handleCurrencyChange('comissaoValor', e.target.value)} 
											className="w-full p-1 border border-gray-300 rounded-md text-xs"
											placeholder="R$ 0,00"
										/>
									</div>
								</div>
								<div>
									<label className="block text-xs font-medium text-gray-700 mb-0.5">Adicional</label>
									<input type="text" value={formData.adicional} onChange={(e) => handleInputChange('adicional', e.target.value)} className="w-full p-1 border border-gray-300 rounded-md text-xs" />
								</div>
							</div>
						</div>

						{/* Cliente content */}
						<div className="border rounded p-3 bg-white">
							<h4 className="text-xs font-medium mb-2">Informações do Cliente</h4>
							<div className="space-y-2">
								<div>
									<label className="block text-xs font-medium text-gray-700 mb-0.5">CPF/CNPJ</label>
									<input type="text" value={formData.cpfCnpj} onChange={(e) => handleInputChange('cpfCnpj', e.target.value)} className="w-full p-1 border border-gray-300 rounded-md text-xs" />
								</div>
								<div>
									<label className="block text-xs font-medium text-gray-700 mb-0.5">Nome</label>
									<input type="text" value={formData.nome} onChange={(e) => handleInputChange('nome', e.target.value)} className="w-full p-1 border border-gray-300 rounded-md text-xs" />
								</div>
								<div>
									<label className="block text-xs font-medium text-gray-700 mb-0.5">E-mail</label>
									<input type="email" value={formData.email} onChange={(e) => handleInputChange('email', e.target.value)} className="w-full p-1 border border-gray-300 rounded-md text-xs" />
								</div>
								<div>
									<label className="block text-xs font-medium text-gray-700 mb-0.5">Telefone</label>
									<input type="text" value={formData.telefone} onChange={(e) => handleInputChange('telefone', e.target.value)} className="w-full p-1 border border-gray-300 rounded-md text-xs" />
								</div>
								<div className="grid grid-cols-2 gap-2">
									<div>
										<label className="block text-xs font-medium text-gray-700 mb-0.5">Id Digisac</label>
										<input type="text" value={formData.idDigisac} onChange={(e) => handleInputChange('idDigisac', e.target.value)} className="w-full p-1 border border-gray-300 rounded-md text-xs" />
									</div>
									<div>
										<label className="block text-xs font-medium text-gray-700 mb-0.5">controle</label>
										<input type="text" value={formData.controle} onChange={(e) => handleInputChange('controle', e.target.value)} className="w-full p-1 border border-gray-300 rounded-md text-xs" />
									</div>
								</div>
							</div>
						</div>
					</div>

					{/* Informação Complementar e Parcelas */}
					<div className="border rounded p-3 bg-white">
						<div className="grid grid-cols-1 lg:grid-cols-2 gap-4 mb-3">
							{/* Informação Complementar */}
							<div className="border rounded p-2 bg-gray-50">
								<label className="block text-xs font-medium text-gray-700 mb-0.5">Informação Complementar sobre a Apólice</label>
								<textarea value={formData.informacoesComplementares} onChange={(e) => handleInputChange('informacoesComplementares', e.target.value)} rows={6} className="w-full p-1 border border-gray-300 rounded-md text-xs" />
							</div>

							{/* Espaço para informações adicionais ou vazio */}
							<div className="border rounded p-2 bg-gray-50">
								<div className="space-y-2">
									<div>
										<label className="block text-xs font-medium text-gray-700 mb-0.5">Data Cancelamento</label>
										<input type="date" value={formData.dataCancel} onChange={(e) => handleInputChange('dataCancel', e.target.value)} className="w-full p-1 border border-gray-300 rounded-md text-xs" />
									</div>
									<div>
										<label className="block text-xs font-medium text-gray-700 mb-0.5">Motivo Cancelamento</label>
										<textarea value={formData.motivo} onChange={(e) => handleInputChange('motivo', e.target.value)} rows={3} className="w-full p-1 border border-gray-300 rounded-md text-xs" />
									</div>
								</div>
							</div>
						</div>

						{/* Parcelas */}
						<div ref={parcelasSectionRef} className="pt-2">
							{/* Campos de controle de parcelas - acima da tabela */}
							<div className="flex items-center gap-4 mb-3 p-2 bg-gray-50 rounded border">
								<div className="flex items-center gap-2">
									<label className="text-xs font-medium text-gray-700">Parcelas</label>
									<input 
										type="number" 
										value={formData.parcelas} 
										onChange={(e) => handleInputChange('parcelas', e.target.value)} 
										className="w-20 p-1 border border-gray-300 rounded text-xs focus:border-purple-500 focus:ring-1 focus:ring-purple-500" 
									/>
								</div>
								<div className="flex items-center gap-2">
									<label className="text-xs font-medium text-gray-700">Forma Pgto</label>
									<select 
										value={formData.pagamentoId ?? ''} 
										onChange={(e) => handleInputChange('pagamentoId', e.target.value)} 
										className="p-1 border border-gray-300 rounded text-xs w-56 max-w-full focus:border-purple-500 focus:ring-1 focus:ring-purple-500"
									>
										<option value="">--</option>
										{catalogs.pagamentos && catalogs.pagamentos.length > 0 ? (
											catalogs.pagamentos.map((pm) => {
												let label: string;
												if (typeof pm === 'string') label = pm;
												else if (pm && typeof pm === 'object') {
													label = pm?.nome ?? pm?.tipoPagamento ?? pm?.descricao ?? pm?.label ?? pm?.nomePagamento ?? JSON.stringify(pm);
												} else {
													label = String(pm);
												}
												const value = (pm && typeof pm === 'object' && (pm.id ?? pm.value ?? pm.nome)) || (typeof pm === 'string' ? pm : String(pm));
												return <option key={value || label} value={value}>{label}</option>;
											})
										) : (
											<>
												<option value="CARTÃO DE CRÉDITO">CARTÃO DE CRÉDITO</option>
												<option value="BOLETO">BOLETO</option>
												<option value="PIX">PIX</option>
											</>
										)}
									</select>
								</div>
							</div>
							
							{/* Exibir parcelas existentes do servidor (se houver) */}
							{parcelasApi && parcelasApi.length > 0 && (
								<>
								<div className="mb-3">
									<h5 className="text-xs font-medium mb-2">Parcelas</h5>
									<div className="overflow-x-auto border rounded">
										<table className="min-w-full bg-white text-[11px]">
											<thead className="bg-purple-200">
												<tr>
													<th className="px-3 py-2 text-left text-xs text-indigo-800 font-medium">#</th>
													<th className="px-3 py-2 text-left text-xs text-indigo-800 font-medium">Documento</th>
													<th className="px-3 py-2 text-left text-xs text-indigo-800 font-medium">Parcela</th>
													<th className="px-3 py-2 text-left text-xs text-indigo-800 font-medium">Vencimento</th>
													<th className="px-3 py-2 text-left text-xs text-indigo-800 font-medium">Pagamento</th>
													<th className="px-3 py-2 text-left text-xs text-indigo-800 font-medium">Pago</th>
													<th className="px-3 py-2 text-left text-xs text-indigo-800 font-medium">Observações</th>
													<th className="px-3 py-2 text-center text-xs text-indigo-800 font-medium">ações</th>
												</tr>
											</thead>
											<tbody className="divide-y divide-gray-200">
												{parcelasApi.map((p, idx) => (
													<tr key={p.id || idx} className="hover:bg-gray-50">
														<td className="px-3 py-1.5 text-xs text-gray-900">{idx + 1}</td>
														<td className="px-3 py-1.5 text-xs text-gray-900">{p.numeroDocumento || p.codigoProposta}</td>
														<td className="px-3 py-1.5 text-xs text-gray-900">{p.numeroParcela}</td>
														{/* Vencimento: editable when in inline edit */}
														<td className="px-3 py-1.5 text-xs text-gray-900">
															{editingParcelaId === p.id ? (
																<input
																	type="date"
																	value={toInputDate(parcelaDraft?.dataVencimento ?? p.dataVencimento)}
																	onChange={(e) => setParcelaDraft((d) => ({ ...(d || {}), dataVencimento: e.target.value }))}
																	className="w-full p-1 border rounded text-xs"
																	disabled={!!savingParcelaById[p.id as any]}
																/>
															) : (
																<div onClick={() => startEditingParcela(p)} className="cursor-pointer">{p.dataVencimento ? new Date(p.dataVencimento).toLocaleDateString() : ''}</div>
															)}
														</td>
														{/* Data de pagamento: editable */}
														<td className="px-3 py-1.5 text-xs text-gray-900">
															{editingParcelaId === p.id ? (
																<input
																	type="date"
																	value={toInputDate(parcelaDraft?.dataPagamento ?? p.dataPagamento)}
																	onChange={(e) => setParcelaDraft((d) => ({ ...(d || {}), dataPagamento: e.target.value }))}
																	className="w-full p-1 border rounded text-xs"
																	disabled={!!savingParcelaById[p.id as any]}
																/>
															) : (
																<div onClick={() => startEditingParcela(p)} className="cursor-pointer">{p.dataPagamento ? new Date(p.dataPagamento).toLocaleDateString() : ''}</div>
															)}
														</td>
														{/* Pago checkbox */}
														<td className="px-3 py-1.5 text-xs text-gray-900">
															{editingParcelaId === p.id ? (
																<input
																	type="checkbox"
																	checked={!!(parcelaDraft?.pago ?? p.pago)}
																	onChange={(e) => { const checked = e.target.checked; /* fire-and-forget */ handleToggleParcelaPago(p, checked); }}
																	disabled={!!savingParcelaById[p.id as any]}
																/>
															) : (
																<div onClick={() => startEditingParcela(p)} className="cursor-pointer">{p.pago ? 'Sim' : 'Não'}</div>
															)}
														</td>
														{/* Observações editable */}
														<td className="px-3 py-1.5 text-xs text-gray-900">
															{editingParcelaId === p.id ? (
																<textarea
																	rows={2}
																	value={parcelaDraft?.observacoes ?? p.observacoes ?? ''}
																	onChange={(e) => setParcelaDraft((d) => ({ ...(d || {}), observacoes: e.target.value }))}
																	className="w-full p-1 border rounded text-xs"
																	disabled={!!savingParcelaById[p.id as any]}
																/>
															) : (
																(p.observacoes || '')
															)}
														</td>
														<td className="px-3 py-2 text-xs text-gray-900 text-center">
															{editingParcelaId === p.id ? (
																<div className="flex items-center justify-center gap-2">
																	<button
																		type="button"
																		className="px-2 py-1  text-white rounded text-xs flex items-center gap-2" style={{ backgroundColor: "#7A61B2" }}
																		onClick={async () => {
																			if (!editingParcelaId) return;
																			try {
																				// set per-row saving
																				setSavingParcelaById((s) => ({ ...s, [editingParcelaId!]: true }));
																				const payload: any = {
																				// backend expects full timestamp/ISO strings
																				dataVencimento: toSaoPauloTimestamp(parcelaDraft?.dataVencimento) ?? null,
																				dataPagamento: toSaoPauloTimestamp(parcelaDraft?.dataPagamento) ?? null,
																				pago: !!parcelaDraft?.pago,
																				observacoes: parcelaDraft?.observacoes || undefined,
																			};
																				await parcelaService.update(editingParcelaId as any, payload);
																				// optimistic update locally
																				setParcelasApi((prev) => prev.map((x) => (x.id === editingParcelaId ? { ...(x as any), ...payload } as any : x)));
																				notifier.show({ message: 'Parcela atualizada', severity: 'success' });
																				setEditingParcelaId(null);
																				setParcelaDraft(null);
																			} catch (err: any) {
																				notifier.show({ message: err?.message || 'Erro ao atualizar parcela', severity: 'error' });
																			} finally {
																				setSavingParcelaById((s) => {
																					const copy = { ...(s || {}) };
																					delete copy[editingParcelaId as any];
																					return copy;
																				});
																			}
																	}}
																	>
																		{savingParcelaById[editingParcelaId as any] ? 'Salvando...' : 'Salvar'}
																		</button>
																		<button type="button" className="px-2 py-1 bg-gray-100 rounded text-xs" onClick={() => { setEditingParcelaId(null); setParcelaDraft(null); }}>Cancelar</button>
																	</div>
															) : (
																<div className="flex items-center justify-center gap-2">
																	{/* Edit icon button */}
																	<button
																		type="button"
																		onClick={() => { setEditingParcelaId(p.id); setParcelaDraft({ dataVencimento: p.dataVencimento, dataPagamento: p.dataPagamento, pago: !!p.pago, observacoes: p.observacoes }); }}
																		className="p-1 rounded hover:bg-gray-100 disabled:opacity-50"
																		aria-label={`Editar parcela ${p.id || ''}`}
																		disabled={!!savingParcelaById[p.id as any]}
																	>
																		<svg className="w-4 h-4 text-indigo-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
																			<path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5" />
																			<path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M18.5 2.5a2.121 2.121 0 013 3L12 15l-4 1 1-4 9.5-9.5z" />
																		</svg>
																	</button>

																	{/* Delete icon button */}
																	<button
																		type="button"
																		onClick={() => handleDeleteParcela(p)}
																		className="p-1 rounded hover:bg-gray-100 disabled:opacity-50"
																		aria-label={`Excluir parcela ${p.id || ''}`}
																		disabled={!!savingParcelaById[p.id as any]}
																		>
																		<svg className="w-4 h-4 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
																			<path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6M9 7V4a1 1 0 011-1h4a1 1 0 011 1v3" />
																		</svg>
																	</button>
																</div>
															)}
														</td>
													</tr>
												))}
												
												{/* Linha inline para criar nova parcela */}
												{isCreatingNewParcela && (
													<tr className="bg-blue-50">
														<td className="px-3 py-1.5 text-xs">
															<span className="text-blue-600 font-medium">Novo</span>
														</td>
														<td className="px-3 py-1.5 text-xs">
															<input
																type="text"
																value={newParcelaDraft.numeroDocumento || formData.proposta || formData.apolice || ''}
																onChange={(e) => setNewParcelaDraft({...newParcelaDraft, numeroDocumento: e.target.value})}
																className="w-full p-1 border rounded text-xs"
																placeholder="Documento"
															/>
														</td>
														<td className="px-3 py-1.5 text-xs">
															<input
																type="text"
																value={newParcelaDraft.numeroParcela || ''}
																onChange={(e) => setNewParcelaDraft({...newParcelaDraft, numeroParcela: e.target.value})}
																className="w-full p-1 border rounded text-xs"
																placeholder="Número"
															/>
														</td>
														<td className="px-3 py-1.5 text-xs">
															<input
																type="date"
																value={newParcelaDraft.dataVencimento || ''}
																onChange={(e) => setNewParcelaDraft({...newParcelaDraft, dataVencimento: e.target.value})}
																className="w-full p-1 border rounded text-xs"
															/>
														</td>
														<td className="px-3 py-1.5 text-xs">
															<input
																type="date"
																value={newParcelaDraft.dataPagamento || ''}
																onChange={(e) => setNewParcelaDraft({...newParcelaDraft, dataPagamento: e.target.value})}
																className="w-full p-1 border rounded text-xs"
															/>
														</td>
														<td className="px-3 py-1.5 text-xs">
															<input
																type="checkbox"
																checked={newParcelaDraft.pago || false}
																onChange={(e) => setNewParcelaDraft({...newParcelaDraft, pago: e.target.checked})}
																className="rounded"
															/>
														</td>
														<td className="px-3 py-1.5 text-xs">
															<div className="flex gap-2">
																<button
																	type="button"
																	className="px-2 py-1  text-white rounded text-xs" style={{ backgroundColor: "#7A61B2" }}
																	onClick={async () => {
																		try {
																			// Calcular próximo número de parcela se não informado
																			let proximoNumero = newParcelaDraft.numeroParcela;
																			if (!proximoNumero && parcelasApi.length > 0) {
																				const maiorNumero = Math.max(...parcelasApi.map(p => parseInt(String(p.numeroParcela || '0'))));
																				proximoNumero = String(maiorNumero + 1);
																			} else if (!proximoNumero) {
																				proximoNumero = '1';
																			}
																			
																			const payload = {
																				documentoId: apolice?.id,
																				sequencia: parseInt(proximoNumero),
																				numeroParcela: proximoNumero,
																				numeroDocumento: newParcelaDraft.numeroDocumento || formData.proposta || formData.apolice || '',
																				dataVencimento: newParcelaDraft.dataVencimento ? toSaoPauloTimestamp(newParcelaDraft.dataVencimento) : undefined,
																				dataPagamento: newParcelaDraft.dataPagamento ? toSaoPauloTimestamp(newParcelaDraft.dataPagamento) : undefined,
																				pago: newParcelaDraft.pago || false,
																				observacoes: newParcelaDraft.observacoes || '',
																				agendado: false,
																				// valorParcela intentionally omitted unless explicitly required
																			};

																			await parcelaService.create(payload);
																			// Recarregar documento completo com todos os relacionamentos
																			await reloadDocumentWithRelations(apolice?.id);

																			notifier.show({ message: 'Parcela criada com sucesso!', severity: 'success' });
																			setIsCreatingNewParcela(false);
																			setNewParcelaDraft({});
																		} catch (error) {
																			console.error('Erro ao criar parcela:', error);
																			notifier.show({ message: 'Erro ao criar parcela', severity: 'error' });
																		}
																	}}
																>
																	Salvar
																</button>
																<button
																	type="button"
																	className="px-2 py-1 bg-gray-100 rounded text-xs"
																	onClick={() => {
																		setIsCreatingNewParcela(false);
																		setNewParcelaDraft({});
																	}}
																>
																	Cancelar
																</button>
															</div>
														</td>
													</tr>
												)}
											</tbody>
										</table>
									</div>
								</div>
								
								{/* Botões para criar novas parcelas - abaixo da tabela */}
								<div className="flex items-center justify-between mb-2">
									<button
										type="button"
										onClick={() => setIsCreatingNewParcela(true)}
										className="px-3 py-1.5  hover:bg-purple-500 text-white rounded-lg text-xs font-medium transition-colors flex items-center gap-2" style={{ backgroundColor: "#7A61B2" }}
										disabled={isCreatingNewParcela}
									>
										<svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
											<path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
										</svg>
										{isCreatingNewParcela ? 'Editando...' : 'Nova Parcela'}
									</button>
									<button
										type="button"
										onClick={() => setShowNovaParcelaModal(true)}
										className="px-3 py-1.5  hover:bg-purple-500 text-white rounded-lg text-xs font-medium transition-colors flex items-center gap-2" style={{ backgroundColor: "#7A61B2" }}
									>
										<svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
											<path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
										</svg>
										Múltiplas Parcelas
									</button>
								</div>
								</>
							)}

							{/* Seção para quando não há parcelas - mostrar botão de criação */}
							{(!parcelasApi || parcelasApi.length === 0) && (
								<div className="mb-3">
									<h5 className="text-xs font-medium mb-2">Parcelas</h5>
									
									{/* Botões para criar parcelas quando não há nenhuma */}
									<div className="flex items-center justify-between mb-2">
										<button
											type="button"
											onClick={() => setIsCreatingNewParcela(true)}
											className="px-3 py-1.5  hover:bg-purple-500 text-white rounded-lg text-xs font-medium transition-colors flex items-center gap-2" style={{ backgroundColor: "#7A61B2" }}
											disabled={isCreatingNewParcela}
										>
											<svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
												<path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
											</svg>
											{isCreatingNewParcela ? 'Editando...' : 'Nova Parcela'}
										</button>
										<button
											type="button"
											onClick={() => setShowNovaParcelaModal(true)}
											className="px-3 py-1.5  hover:bg-purple-500 text-white rounded-lg text-xs font-medium transition-colors flex items-center gap-2" style={{ backgroundColor: "#7A61B2" }}
										>
											<svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
												<path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
											</svg>
											Múltiplas Parcelas
										</button>
									</div>
									
									{/* Tabela vazia com linha inline se estiver criando */}
									{isCreatingNewParcela && (
										<div className="overflow-x-auto border rounded">
											<table className="min-w-full bg-white text-[11px]">
												<thead className="bg-gray-100">
													<tr>
														<th className="px-3 py-1.5 text-left text-xs">#</th>
														<th className="px-3 py-1.5 text-left text-xs">Documento</th>
														<th className="px-3 py-1.5 text-left text-xs">Parcela</th>
														<th className="px-3 py-1.5 text-left text-xs">Vencimento</th>
														<th className="px-3 py-1.5 text-left text-xs">Pagamento</th>
														<th className="px-3 py-1.5 text-left text-xs">Pago</th>
														<th className="px-3 py-1.5 text-left text-xs">Observações</th>
													</tr>
												</thead>
												<tbody className="divide-y divide-gray-200">
													<tr className="bg-blue-50">
														<td className="px-3 py-1.5 text-xs">
															<span className="text-blue-600 font-medium">Novo</span>
														</td>
														<td className="px-3 py-1.5 text-xs">
															<input
																type="text"
																value={newParcelaDraft.numeroDocumento || formData.proposta || formData.apolice || ''}
																onChange={(e) => setNewParcelaDraft({...newParcelaDraft, numeroDocumento: e.target.value})}
																className="w-full p-1 border rounded text-xs"
																placeholder="Documento"
															/>
														</td>
														<td className="px-3 py-1.5 text-xs">
															<input
																type="text"
																value={newParcelaDraft.numeroParcela || ''}
																onChange={(e) => setNewParcelaDraft({...newParcelaDraft, numeroParcela: e.target.value})}
																className="w-full p-1 border rounded text-xs"
																placeholder="Número"
															/>
														</td>
														<td className="px-3 py-1.5 text-xs">
															<input
																type="date"
																value={newParcelaDraft.dataVencimento || ''}
																onChange={(e) => setNewParcelaDraft({...newParcelaDraft, dataVencimento: e.target.value})}
																className="w-full p-1 border rounded text-xs"
															/>
														</td>
														<td className="px-3 py-1.5 text-xs">
															<input
																type="date"
																value={newParcelaDraft.dataPagamento || ''}
																onChange={(e) => setNewParcelaDraft({...newParcelaDraft, dataPagamento: e.target.value})}
																className="w-full p-1 border rounded text-xs"
															/>
														</td>
														<td className="px-3 py-1.5 text-xs">
															<input
																type="checkbox"
																checked={newParcelaDraft.pago || false}
																onChange={(e) => setNewParcelaDraft({...newParcelaDraft, pago: e.target.checked})}
																className="rounded"
															/>
														</td>
														<td className="px-3 py-1.5 text-xs">
															<div className="flex gap-2">
																<button
																	type="button"
																	className="px-2 py-1  text-white rounded text-xs" style={{ backgroundColor: "#7A61B2" }}
																	onClick={async () => {
																		try {
																			const payload = {
																				documentoId: apolice?.id,
																				sequencia: 1,
																				numeroParcela: newParcelaDraft.numeroParcela || '1',
																				numeroDocumento: newParcelaDraft.numeroDocumento || formData.proposta || formData.apolice || '',
																				dataVencimento: newParcelaDraft.dataVencimento ? toSaoPauloTimestamp(newParcelaDraft.dataVencimento) : undefined,
																				dataPagamento: newParcelaDraft.dataPagamento ? toSaoPauloTimestamp(newParcelaDraft.dataPagamento) : undefined,
																				pago: newParcelaDraft.pago || false,
																				observacoes: newParcelaDraft.observacoes || '',
																				agendado: false,
																				// valorParcela intentionally omitted unless explicitly required
																			};

																			await parcelaService.create(payload);
																			await reloadDocumentWithRelations(apolice?.id);

																			notifier.show({ message: 'Parcela criada com sucesso!', severity: 'success' });
																			setIsCreatingNewParcela(false);
																			setNewParcelaDraft({});
																		} catch (error) {
																			console.error('Erro ao criar parcela:', error);
																			notifier.show({ message: 'Erro ao criar parcela', severity: 'error' });
																		}
																	}}
																>
																	Salvar
																</button>
																<button
																	type="button"
																	className="px-2 py-1 bg-gray-100 rounded text-xs"
																	onClick={() => {
																		setIsCreatingNewParcela(false);
																		setNewParcelaDraft({});
																	}}
																>
																	Cancelar
																</button>
															</div>
														</td>
													</tr>
												</tbody>
											</table>
										</div>
									)}
								</div>
							)}

							{/* Removed non-integrated local parcelas table: server-backed parcelasApi table above is used instead. */}
						</div>

						{/* Repasses */}
						<div className="pt-2">
							{/* Repasse summary fields will be displayed below the repasses table */}
							{/* Exibir repasses existentes do servidor (se houver) */}
							{repassesApi && repassesApi.length > 0 && (
								<>
								<div className="mb-3">
									<h5 className="text-xs font-medium mb-2">💰 Repasses do Documento</h5>
									<div className="overflow-x-auto border rounded">
										<table className="min-w-full bg-white text-[11px]">
											<thead className="bg-purple-200">
												<tr>
													<th className="px-3 py-2 text-left text-xs text-indigo-800 font-medium">Proposta</th>
													<th className="px-3 py-2 text-left text-xs text-indigo-800 font-medium">Data Pagamento</th>
													<th className="px-3 py-2 text-left text-xs text-indigo-800 font-medium">Valor Pago</th>
													<th className="px-3 py-2 text-left text-xs text-indigo-800 font-medium">Informação</th>
													<th className="px-3 py-2 text-left text-xs text-indigo-800 font-medium">Documento Origem</th>
													<th className="px-3 py-2 text-center text-xs text-indigo-800 font-medium">ações</th>
												</tr>
											</thead>
											<tbody className="divide-y divide-gray-200">
												{repassesApi.map((r, idx) => (
													<tr key={r.id || idx} className="hover:bg-gray-50">
														<td className="px-3 py-1.5 text-xs text-gray-900">
															{editingRepasseId === r.id ? (
																<input
																	type="text"
																	value={repasseDraft?.numeroDocumento || ''}
																	onChange={(e) => setRepasseDraft({...repasseDraft, numeroDocumento: e.target.value})}
																	className="w-full p-1 border rounded text-xs"
																	disabled={!!savingRepasseById[r.id as any]}
																/>
															) : (
																<div onClick={() => startEditingRepasse(r)} className="cursor-pointer">{r.numeroDocumento || '-'}</div>
															)}
														</td>
														<td className="px-3 py-1.5 text-xs text-gray-900">
															{editingRepasseId === r.id ? (
																<input
																	type="date"
																	value={toInputDate(repasseDraft?.dataPagamento)}
																	onChange={(e) => setRepasseDraft({...repasseDraft, dataPagamento: e.target.value})}
																	className="w-full p-1 border rounded text-xs"
																	disabled={!!savingRepasseById[r.id as any]}
																/>
															) : (
																<div onClick={() => startEditingRepasse(r)} className="cursor-pointer">
																	{r.dataPagamento ? new Date(r.dataPagamento).toLocaleDateString() : '-'}
																</div>
															)}
														</td>
														<td className="px-3 py-1.5 text-xs text-gray-900">
															{editingRepasseId === r.id ? (
																<input
																	type="number"
																	step="0.01"
																	value={repasseDraft?.repassePago || ''}
																	onChange={(e) => setRepasseDraft({...repasseDraft, repassePago: parseFloat(e.target.value) || 0})}
																	className="w-full p-1 border rounded text-xs"
																	disabled={!!savingRepasseById[r.id as any]}
																/>
															) : (
																<div onClick={() => startEditingRepasse(r)} className="cursor-pointer">
																	R$ {formatNumber(r.repassePago, 2)}
																</div>
															)}
														</td>
														<td className="px-3 py-1.5 text-xs text-gray-900">
															{editingRepasseId === r.id ? (
																<input
																	type="text"
																	value={repasseDraft?.informacao || ''}
																	onChange={(e) => setRepasseDraft({...repasseDraft, informacao: e.target.value})}
																	className="w-full p-1 border rounded text-xs"
																	disabled={!!savingRepasseById[r.id as any]}
																/>
															) : (
																<div onClick={() => startEditingRepasse(r)} className="cursor-pointer">{r.informacao || '-'}</div>
															)}
														</td>
														<td className="px-3 py-1.5 text-xs text-gray-900">
															{editingRepasseId === r.id ? (
																<input
																	type="text"
																	value={repasseDraft?.documentoOrigem || ''}
																	onChange={(e) => setRepasseDraft({...repasseDraft, documentoOrigem: e.target.value})}
																	className="w-full p-1 border rounded text-xs"
																	disabled={!!savingRepasseById[r.id as any]}
																/>
															) : (
																<div onClick={() => startEditingRepasse(r)} className="cursor-pointer">{r.documentoOrigem || '-'}</div>
															)}
														</td>
														<td className="px-3 py-1.5 text-xs text-gray-900">
															{editingRepasseId === r.id ? (
																<div className="flex gap-2">
																	<button
																		type="button"
																		className="px-2 py-1  text-white rounded text-xs" style={{ backgroundColor: "#7A61B2" }}
																		onClick={async () => {
																			if (!editingRepasseId || !repasseDraft) return;
																			try {
																				setSavingRepasseById(s => ({ ...s, [editingRepasseId]: true }));
																				const payload = {
																					numeroDocumento: repasseDraft.numeroDocumento,
																					dataPagamento: toSaoPauloTimestamp(repasseDraft.dataPagamento),
																					repassePago: repasseDraft.repassePago,
																					informacao: repasseDraft.informacao,
																					documentoOrigem: repasseDraft.documentoOrigem
																				};
																				await repasseService.update(String(editingRepasseId), payload);
																				setRepassesApi(prev => prev.map(x => x.id === editingRepasseId ? { ...x, ...payload } : x));
																				notifier.show({ message: 'Repasse atualizado', severity: 'success' });
																				setEditingRepasseId(null);
																				setRepasseDraft(null);
																			} catch (err: any) {
																				notifier.show({ message: err?.message || 'Erro ao atualizar repasse', severity: 'error' });
																			} finally {
																				setSavingRepasseById(s => {
																					const copy = { ...s };
																					delete copy[editingRepasseId];
																					return copy;
																				});
																			}
																		}}
																		disabled={!!savingRepasseById[editingRepasseId as any]}
																	>
																		{savingRepasseById[editingRepasseId as any] ? 'Salvando...' : 'Salvar'}
																	</button>
																	<button
																		type="button"
																		className="px-2 py-1 bg-gray-100 rounded text-xs"
																		onClick={() => { setEditingRepasseId(null); setRepasseDraft(null); }}
																	>
																		Cancelar
																	</button>
																</div>
															) : (
																<div className="flex items-center gap-2">
																	<button
																		type="button"
																		onClick={() => startEditingRepasse(r)}
																		className="p-1 rounded hover:bg-gray-100 disabled:opacity-50"
																		aria-label={`Editar repasse ${r.id || ''}`}
																		disabled={!!savingRepasseById[r.id as any]}
																	>
																		<svg className="w-4 h-4 text-indigo-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
																			<path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5" />
																			<path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M18.5 2.5a2.121 2.121 0 013 3L12 15l-4 1 1-4 9.5-9.5z" />
																		</svg>
																	</button>
																	<button
																		type="button"
																		onClick={() => handleDeleteRepasse(r)}
																		className="p-1 rounded hover:bg-gray-100 disabled:opacity-50"
																		aria-label={`Excluir repasse ${r.id || ''}`}
																		disabled={!!savingRepasseById[r.id as any]}
																	>
																		<svg className="w-4 h-4 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
																			<path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6M9 7V4a1 1 0 011-1h4a1 1 0 011 1v3" />
																		</svg>
																	</button>
																</div>
															)}
														</td>
													</tr>
												))}
												
												{/* Nova linha para criação de repasse */}
												{isCreatingNewRepasse && (
													<tr className="bg-green-50 border-2 border-green-200">
														<td className="px-3 py-1.5 text-xs">
															<input
																type="text"
																value={newRepasseDraft.numeroDocumento || ''}
																onChange={(e) => setNewRepasseDraft({...newRepasseDraft, numeroDocumento: e.target.value})}
																className="w-full p-1 border rounded text-xs"
																placeholder="Número da proposta"
															/>
														</td>
														<td className="px-3 py-1.5 text-xs">
															<input
																type="date"
																value={newRepasseDraft.dataPagamento || ''}
																onChange={(e) => setNewRepasseDraft({...newRepasseDraft, dataPagamento: e.target.value})}
																className="w-full p-1 border rounded text-xs"
															/>
														</td>
														<td className="px-3 py-1.5 text-xs">
															<input
																type="number"
																step="0.01"
																value={newRepasseDraft.repassePago || ''}
																onChange={(e) => setNewRepasseDraft({...newRepasseDraft, repassePago: e.target.value})}
																className="w-full p-1 border rounded text-xs"
																placeholder="0.00"
															/>
														</td>
														<td className="px-3 py-1.5 text-xs">
															<input
																type="text"
																value={newRepasseDraft.informacao || ''}
																onChange={(e) => setNewRepasseDraft({...newRepasseDraft, informacao: e.target.value})}
																className="w-full p-1 border rounded text-xs"
																placeholder="Informação"
															/>
														</td>
														<td className="px-3 py-1.5 text-xs">
															<input
																type="text"
																value={newRepasseDraft.documentoOrigem || ''}
																onChange={(e) => setNewRepasseDraft({...newRepasseDraft, documentoOrigem: e.target.value})}
																className="w-full p-1 border rounded text-xs"
																placeholder="Documento origem"
															/>
														</td>
														<td className="px-3 py-1.5 text-xs">
															<div className="flex gap-2">
																<button
																	type="button"
																	className="px-2 py-1  text-white rounded text-xs" style={{ backgroundColor: "#7A61B2" }}
																	onClick={async () => {
																		try {
																			const payload = {
																				documentoId: apolice?.id,
																				empresaId: (apolice as any)?.empresaId || undefined,
																				repassePago: newRepasseDraft.repassePago,
																				cliente: formData.cpfCnpj || (formData as any).cpfCnpjCliente || '',
																				cpfCnpjCliente: formData.cpfCnpj || (formData as any).cpfCnpjCliente || '',
																				dataPagamento: toSaoPauloTimestamp(newRepasseDraft.dataPagamento),
																				numeroDocumento: newRepasseDraft.numeroDocumento || (apolice as any)?.numeroDocumento || (apolice as any)?.codigoProposta || formData.proposta || formData.apolice || '',
																				apolice: formData.apolice || '',
																				endosso: formData.endosso || '',
																				documentoOrigem: newRepasseDraft.documentoOrigem || `repasse ${new Date().toISOString().split('T')[0]}`,
																				informacao: newRepasseDraft.informacao
																			};
																			await repasseService.create(payload);
																			
																			// Recarregar documento completo com todos os relacionamentos
																			await reloadDocumentWithRelations(apolice?.id);
																			
																			notifier.show({ message: 'Repasse criado', severity: 'success' });
																			setIsCreatingNewRepasse(false);
																			setNewRepasseDraft({});
																		} catch (err: any) {
																			notifier.show({ message: err?.message || 'Erro ao criar repasse', severity: 'error' });
																		}
																	}}
																>
																	Salvar
																</button>
																<button
																	type="button"
																	className="px-2 py-1 bg-gray-100 rounded text-xs"
																	onClick={() => {
																		setIsCreatingNewRepasse(false);
																		setNewRepasseDraft({});
																	}}
																>
																	Cancelar
																</button>
															</div>
														</td>
													</tr>
												)}
											</tbody>
										</table>
									</div>
								</div>
                                
								{/* Repasse summary fields: taxaComissao, conta, totalRepassePago, valorFinal (displayed below table) */}
								<div className="mt-2 mb-3 grid grid-cols-4 gap-3">
									<div className="border rounded p-2 bg-white">
										<label className="block text-xs font-medium text-gray-700 mb-1">Taxa Comissão (Seguradora)</label>
										{/* show taxa as percentage (server provides 10 for 10%) */}
										<div className="text-sm font-medium">
											{(() => {
												if (taxaComissao === null || typeof taxaComissao === 'undefined' || taxaComissao === '') return '-';
												try {
													const raw = String(taxaComissao).trim();
													if (raw.endsWith('%')) return raw;
													const n = Number(raw.replace(/[^0-9.-]+/g, ''));
													if (Number.isNaN(n)) return raw;
													// show without decimals when integer, otherwise two decimals
													return `${n % 1 === 0 ? n : n.toFixed(2)}%`;
												} catch (e) {
													return String(taxaComissao);
												}
											})()}
										</div>
									</div>
									<div className="border rounded p-2 bg-white">
										<label className="block text-xs font-medium text-gray-700 mb-1">Conta (comissão - taxa %)</label>
										<div className="text-sm font-medium">R$ {formatNumber(Number((conta as any) || 0), 2)}</div>
									</div>
									<div className="border rounded p-2 bg-white">
										<label className="block text-xs font-medium text-gray-700 mb-1">Total Repasses Pago</label>
										<div className="text-sm font-medium">R$ {formatNumber(totalRepassePago, 2)}</div>
									</div>
									<div className="border rounded p-2 bg-white">
										<label className="block text-xs font-medium text-gray-700 mb-1">Valor Final (Conta - Total Repasses)</label>
										<div className="text-sm font-medium">R$ {formatNumber(valorFinal, 2)}</div>
									</div>
								</div>
								</>
							)}
							
							{/* Botão para criar novo repasse */}
							<div className="flex items-center justify-between mb-2">
								<button
									type="button"
									onClick={() => {
										setNewRepasseDraft({
											numeroDocumento: formData.proposta || (apolice as any)?.numeroDocumento || (apolice as any)?.codigoProposta || formData.apolice || '',
											dataPagamento: new Date().toISOString().split('T')[0],
											repassePago: '',
											informacao: '',
											documentoOrigem: ''
										});
										setIsCreatingNewRepasse(true);
									}}
									className="px-3 py-1.5  hover:bg-purple-500 text-white rounded-lg text-xs font-medium transition-colors flex items-center gap-2" style={{ backgroundColor: "#7A61B2" }}
									disabled={isCreatingNewRepasse}
								>
									<svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
										<path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
									</svg>
									{isCreatingNewRepasse ? 'Editando...' : 'Novo Repasse'}
								</button>
								<div className="text-xs text-gray-600">Gestão de repasses...</div>
							</div>
						</div>

						{/* Seção para quando não há repasses - mostrar botão de criação */}
						{(!repassesApi || repassesApi.length === 0) && (
							<div className="mb-3">
								<div className="flex items-center justify-between">
									<h5 className="text-xs font-medium mb-2">💰 Repasses</h5>
									<button
										type="button"
										onClick={() => {
											setNewRepasseDraft({
												numeroDocumento: formData.proposta || (apolice as any)?.numeroDocumento || (apolice as any)?.codigoProposta || formData.apolice || '',
												dataPagamento: new Date().toISOString().split('T')[0],
												repassePago: '',
												informacao: '',
												documentoOrigem: ''
											});
											setIsCreatingNewRepasse(true);
										}}
										className="px-2 py-1  hover:bg-purple-500 text-white rounded text-xs font-medium transition-colors" style={{ backgroundColor: "#7A61B2" }}
									>
										+ Repasse
									</button>
								</div>
								
								{/* Tabela vazia com linha inline se estiver criando */}
								{isCreatingNewRepasse && (
									<div className="overflow-x-auto border rounded">
										<table className="min-w-full bg-white text-[11px]">
											<thead className="bg-gray-100">
												<tr>
													<th className="px-3 py-1.5 text-left text-xs">Proposta</th>
													<th className="px-3 py-1.5 text-left text-xs">Data Pagamento</th>
													<th className="px-3 py-1.5 text-left text-xs">Valor Pago</th>
													<th className="px-3 py-1.5 text-left text-xs">Informação</th>
													<th className="px-3 py-1.5 text-left text-xs">Documento Origem</th>
													<th className="px-3 py-1.5 text-left text-xs">Ações</th>
												</tr>
											</thead>
											<tbody className="divide-y divide-gray-200">
												<tr className="bg-green-50">
													<td className="px-3 py-1.5 text-xs">
														<input
															type="text"
															value={newRepasseDraft.numeroDocumento || ''}
															onChange={(e) => setNewRepasseDraft({...newRepasseDraft, numeroDocumento: e.target.value})}
															className="w-full p-1 border rounded text-xs"
															placeholder="Proposta"
														/>
													</td>
													<td className="px-3 py-1.5 text-xs">
														<input
															type="date"
															value={newRepasseDraft.dataPagamento || ''}
															onChange={(e) => setNewRepasseDraft({...newRepasseDraft, dataPagamento: e.target.value})}
															className="w-full p-1 border rounded text-xs"
														/>
													</td>
													<td className="px-3 py-1.5 text-xs">
														<input
															type="text"
															value={newRepasseDraft.repassePago || ''}
															onChange={(e) => setNewRepasseDraft({...newRepasseDraft, repassePago: e.target.value})}
															className="w-full p-1 border rounded text-xs"
															placeholder="Valor"
														/>
													</td>
													<td className="px-3 py-1.5 text-xs">
														<input
															type="text"
															value={newRepasseDraft.informacao || ''}
															onChange={(e) => setNewRepasseDraft({...newRepasseDraft, informacao: e.target.value})}
															className="w-full p-1 border rounded text-xs"
															placeholder="Informação"
														/>
													</td>
													<td className="px-3 py-1.5 text-xs">
														<input
															type="text"
															value={newRepasseDraft.documentoOrigem || ''}
															onChange={(e) => setNewRepasseDraft({...newRepasseDraft, documentoOrigem: e.target.value})}
															className="w-full p-1 border rounded text-xs"
															placeholder="Documento origem"
														/>
													</td>
													<td className="px-3 py-2 text-xs text-center">
														<div className="flex items-center justify-center gap-2">
															<button
																type="button"
																className="px-2 py-1  text-white rounded text-xs" style={{ backgroundColor: "#7A61B2" }}
																onClick={async () => {
																	try {
																		const payload = {
																			documentoId: apolice?.id,
																			empresaId: (apolice as any)?.empresaId || undefined,
																			repassePago: newRepasseDraft.repassePago,
																			cliente: formData.cpfCnpj || (formData as any).cpfCnpjCliente || '',
																			cpfCnpjCliente: formData.cpfCnpj || (formData as any).cpfCnpjCliente || '',
																			dataPagamento: toSaoPauloTimestamp(newRepasseDraft.dataPagamento),
																			numeroDocumento: newRepasseDraft.numeroDocumento || (apolice as any)?.numeroDocumento || (apolice as any)?.codigoProposta || formData.proposta || formData.apolice || '',
																			apolice: formData.apolice || '',
																			endosso: formData.endosso || '',
																			documentoOrigem: newRepasseDraft.documentoOrigem || `repasse ${new Date().toISOString().split('T')[0]}`,
																			informacao: newRepasseDraft.informacao
																		};
																		await repasseService.create(payload);
																		await reloadDocumentWithRelations(apolice?.id);
																		notifier.show({ message: 'Repasse criado', severity: 'success' });
																		setIsCreatingNewRepasse(false);
																		setNewRepasseDraft({});
																	} catch (err: any) {
																		notifier.show({ message: err?.message || 'Erro ao criar repasse', severity: 'error' });
																	}
																}}
															>
																Salvar
															</button>
															<button
																type="button"
																className="px-2 py-1 bg-gray-100 rounded text-xs"
																onClick={() => {
																	setIsCreatingNewRepasse(false);
																	setNewRepasseDraft({});
																}}
															>
																Cancelar
															</button>
														</div>
													</td>
												</tr>
											</tbody>
										</table>
									</div>
								)}
							</div>
						)}

						{/* Sinistros */}
						<div className="pt-2">
							{/* Exibir sinistros existentes do servidor (se houver) */}
							{sinistrosApi && sinistrosApi.length > 0 && (
								<div className="mb-3">
									<h5 className="text-xs font-medium mb-2">🚨 Sinistros do Documento</h5>
									<div className="overflow-x-auto border rounded">
										<table className="min-w-full bg-white text-[11px]">
											<thead className="bg-purple-200">
												<tr>
													<th className="px-3 py-2 text-left text-xs text-indigo-800 font-medium">Nº Sinistro</th>
													<th className="px-3 py-2 text-left text-xs text-indigo-800 font-medium">Nº Documento</th>
													<th className="px-3 py-2 text-left text-xs text-indigo-800 font-medium">Data</th>
													<th className="px-3 py-2 text-left text-xs text-indigo-800 font-medium">Cliente</th>
													<th className="px-3 py-2 text-left text-xs text-indigo-800 font-medium">Tipo</th>
													<th className="px-3 py-2 text-left text-xs text-indigo-800 font-medium">Valor Franquia</th>
													<th className="px-3 py-2 text-left text-xs text-indigo-800 font-medium">Status</th>
													<th className="px-3 py-2 text-center text-xs text-indigo-800 font-medium">Ações</th>
												</tr>
											</thead>
											<tbody className="divide-y divide-gray-200">
												{sinistrosApi.map((s, idx) => (
													<tr 
														key={s.id || idx} 
														className="hover:bg-gray-50 cursor-pointer" 
														onClick={(e) => {
															// Só abre o modal se não clicou no botão
															if ((e.target as HTMLElement).tagName !== 'BUTTON') {
																handleOpenEditSinistroModal(s);
															}
														}}
													>
														<td className="px-3 py-1.5 text-xs text-gray-900">
															{s.numero_sinistro || '-'}
														</td>
														<td className="px-3 py-1.5 text-xs text-gray-900">
															{s.numeroDocumento || '-'}
														</td>
														<td className="px-3 py-1.5 text-xs text-gray-900">
															{s.data ? new Date(s.data).toLocaleDateString('pt-BR') : '-'}
														</td>
														<td className="px-3 py-1.5 text-xs text-gray-900">
															<div>
																<div className="font-medium">{s.clientes?.[0]?.nome || 'N/A'}</div>
																<div className="text-gray-500 text-xs">{s.cpf_cnpj || '-'}</div>
															</div>
														</td>
														<td className="px-3 py-1.5 text-xs text-gray-900">
															{s.tipo || '-'}
														</td>
														<td className="px-3 py-1.5 text-xs text-gray-900">
															{s.valor_franquia ? `R$ ${Number(s.valor_franquia).toLocaleString('pt-BR', { minimumFractionDigits: 2 })}` : '-'}
														</td>
														<td className="px-3 py-1.5 text-xs">
															<div className="flex flex-wrap gap-1">
																{s.oficina && <span className="bg-purple-50 text-blue-800 text-xs px-1 rounded">Oficina</span>}
																{s.abertura && <span className="bg-yellow-100 text-yellow-800 text-xs px-1 rounded">Abertura</span>}
																{s.documentos && <span className="bg-purple-50 text-purple-800 text-xs px-1 rounded">Docs</span>}
																{s.vistoria_sinistro && <span className="bg-orange-100 text-orange-800 text-xs px-1 rounded">Vistoria</span>}
																{s.analise && <span className="bg-indigo-100 text-indigo-800 text-xs px-1 rounded">Análise</span>}
																{s.aprovacao && <span className="bg-green-100 text-green-800 text-xs px-1 rounded">Aprovação</span>}
																{s.pagamento && <span className="bg-teal-100 text-teal-800 text-xs px-1 rounded">Pagamento</span>}
																{s.concluido && <span className="bg-emerald-100 text-emerald-800 text-xs px-1 rounded">Concluído</span>}
															</div>
														</td>
														<td className="px-3 py-2 text-xs text-gray-900 text-center">
															<button
																type="button"
																onClick={() => handleOpenEditSinistroModal(s)}
																className="px-2 py-1 bg-blue-50 text-blue-600 rounded text-xs hover:bg-purple-50"
															>
																Editar
															</button>
														</td>
													</tr>
												))}
											</tbody>
										</table>
									</div>
								</div>
							)}
							
							{/* Botão para criar novo sinistro */}
							<div className="flex items-center justify-between mb-2">
								<button
									type="button"
									onClick={handleOpenNovoSinistroModal}
									className="px-3 py-1.5 bg-red-600 hover:bg-red-700 text-white rounded-lg text-xs font-medium transition-colors flex items-center gap-2"
								>
									<svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
										<path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
									</svg>
									Novo Sinistro
								</button>
								<div className="text-xs text-gray-600">Gestão de sinistros...</div>
							</div>
						</div>

							{/* Botões de ação estilizados */}
							<div className="bg-white rounded-xl shadow-sm border border-gray-200 p-4 flex justify-between items-center mt-6">
								<div className="flex gap-3">
									<button 
										type="button" 
										disabled={saving} 
										onClick={() => saveToApi(false)} 
										className="px-6 py-2  hover:bg-purple-500 disabled:bg-gray-400 text-white rounded-lg text-xs font-medium transition-colors flex items-center gap-2 shadow-sm" style={{ backgroundColor: "#7A61B2" }}
									>
										<svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
											<path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7H5a2 2 0 00-2 2v9a2 2 0 002 2h14a2 2 0 002-2V9a2 2 0 00-2-2h-3m-1 4l-3-3m0 0l-3 3m3-3v12" />
										</svg>
										{saving ? 'Salvando...' : 'Salvar'}
									</button>
									<button 
										type="button" 
										disabled={saving} 
										onClick={() => saveToApi(true)} 
										className="px-6 py-2  hover:bg-purple-500 disabled:bg-gray-400 text-white rounded-lg text-xs font-medium transition-colors flex items-center gap-2 shadow-sm" style={{ backgroundColor: "#7A61B2" }}
									>
										<svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
											<path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
										</svg>
										{saving ? 'Salvando...' : 'Salvar e fechar'}
									</button>
								</div>
								<div>
									<button 
										type="button" 
										className="px-4 py-2 bg-gray-100 hover:bg-gray-200 text-gray-700 text-xs font-medium rounded-lg transition-colors flex items-center gap-2" 
										onClick={onClose}
									>
										<svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
											<path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
										</svg>
										Cancelar
									</button>
								</div>
							</div>
					</div>
						</form>
					</div>
				</div>

				{/* Modal de confirmação estilizado */}
				{confirmOpen && (
					<div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-60 backdrop-blur-sm">
						<div className="bg-white rounded-2xl shadow-2xl p-6 w-[480px] max-w-[90vw]">
							<div className="flex items-center gap-3 mb-4">
								<div className="w-10 h-10 bg-yellow-100 rounded-full flex items-center justify-center">
									<svg className="w-6 h-6 text-yellow-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
										<path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L4.082 16.5c-.77.833.192 2.5 1.732 2.5z" />
									</svg>
								</div>
								<div>
									<h4 className="text-lg font-semibold text-gray-900">Alterações não salvas</h4>
									<p className="text-sm text-gray-600">Você tem alterações pendentes neste documento</p>
								</div>
							</div>
							<p className="text-sm text-gray-700 mb-6 leading-relaxed">
								Deseja salvar as alterações antes de ir para o próximo registro?
							</p>
							<div className="flex justify-end gap-3">
								<button 
									onClick={onConfirmCancel} 
									className="px-4 py-2 bg-gray-100 hover:bg-gray-200 text-gray-700 rounded-lg text-sm font-medium transition-colors"
								>
									Cancelar
								</button>
								<button 
									onClick={onConfirmDiscard} 
									className="px-4 py-2 bg-red-600 hover:bg-red-700 text-white rounded-lg text-sm font-medium transition-colors"
								>
									Descartar
								</button>
								<button 
									onClick={onConfirmSave} 
									disabled={saving} 
									className="px-4 py-2  hover:bg-purple-500 disabled:bg-gray-400 text-white rounded-lg text-sm font-medium transition-colors" style={{ backgroundColor: "#7A61B2" }}
								>
									{saving ? 'Salvando...' : 'Salvar'}
								</button>
							</div>
						</div>
					</div>
				)}

				{/* Modal: Nova Parcela (geração automática) */}
				{showNovaParcelaModal && (
					<div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-40">
						<div className="bg-white rounded-lg shadow-lg w-96 p-4">
							<h4 className="text-sm font-semibold mb-3">Criar parcelas automaticamente</h4>
							<div className="space-y-3">
								<div>
									<label className="text-xs font-medium">Quantidade</label>
									<input type="number" min={1} value={novaParcelaQuantidade} onChange={(e) => setNovaParcelaQuantidade(Number(e.target.value) || 1)} className="w-full p-2 border rounded text-xs" />
								</div>
								{/* valor total removed: field not supported by backend */}
								<div className="text-xs text-gray-600 bg-gray-50 p-2 rounded">
									<strong>Regras automáticas:</strong><br/>
									• 1ª parcela: 7 dias após emissão<br/>
									• 2ª parcela: 30 dias após emissão<br/>
									• Demais: mensalmente no mesmo dia
								</div>
							</div>
							<div className="flex justify-end gap-2 mt-4">
								<button type="button" className="px-3 py-1 bg-gray-100 rounded text-xs" onClick={() => setShowNovaParcelaModal(false)}>Cancelar</button>
								<button type="button" className="px-3 py-1  text-white rounded text-xs" style={{ backgroundColor: "#7A61B2" }} onClick={() => criarParcelasAutomaticas(novaParcelaQuantidade)}>Criar {novaParcelaQuantidade} parcela{novaParcelaQuantidade > 1 ? 's' : ''}</button>
							</div>
						</div>
					</div>
				)}

				</div>
				</div>

				<AgendamentoFormModal
					isOpen={openAgendamentoModal}
					onClose={() => setOpenAgendamentoModal(false)}
					onSuccess={() => { setOpenAgendamentoModal(false); }}
					documentoNumero={apolice?.numeroDocumento || apolice?.numeroApolice || apolice?.codigoProposta}
					initialDescricaoAgendamento={initialDescricaoAgendamento}
				/>

				{/* Modal de Novo Sinistro */}
				<SinistroModal
					isOpen={showNovoSinistroModal}
					onClose={handleCloseNovoSinistroModal}
					sinistro={novoSinistroDraft}
					onSave={handleSaveNovoSinistro}
				/>

				{/* Modal de Edição de Sinistro */}
				<SinistroModal
					isOpen={showEditSinistroModal}
					onClose={handleCloseEditSinistroModal}
					sinistro={editSinistroDraft}
					onSave={handleSaveEditSinistro}
				/>

				{/* Dialog de confirmação de exclusão de repasse */}
				<ConfirmDialog
					isOpen={showDeleteRepasseConfirm}
					title="Excluir Repasse"
					message="Tem certeza que deseja excluir este repasse?"
					details={repasseToDelete ? [
						`Valor: R$ ${repasseToDelete.valorRepasse || '0,00'}`,
						`Data: ${repasseToDelete.dataRepasse ? new Date(repasseToDelete.dataRepasse).toLocaleDateString('pt-BR') : '-'}`,
						repasseToDelete.informacao ? `Info: ${repasseToDelete.informacao}` : ''
					].filter(Boolean) : []}
					onConfirm={confirmDeleteRepasse}
					onCancel={() => {
						setShowDeleteRepasseConfirm(false);
						setRepasseToDelete(null);
					}}
					confirmText="Excluir"
					cancelText="Cancelar"
					confirmButtonColor="#DC2626"
				/>

				{/* Dialog de confirmação de exclusão de parcela */}
				<ConfirmDialog
					isOpen={showDeleteParcelaConfirm}
					title="Excluir Parcela"
					message="Tem certeza que deseja excluir esta parcela?"
						details={parcelaToDelete ? [
							`Parcela: ${parcelaToDelete?.numeroParcela ?? '-'}`,
							`Vencimento: ${parcelaToDelete?.dataVencimento ? new Date(parcelaToDelete.dataVencimento).toLocaleDateString('pt-BR') : '-'}`,
							parcelaToDelete?.pago ? 'Status: Paga' : 'Status: Não paga'
						] : []}
					onConfirm={confirmDeleteParcela}
					onCancel={() => {
						setShowDeleteParcelaConfirm(false);
						setParcelaToDelete(null);
					}}
					confirmText="Excluir"
					cancelText="Cancelar"
					confirmButtonColor="#DC2626"
				/>

				{/* Dialog para confirmar alteração para 'aberta' ao desmarcar pago */}
				<ConfirmDialog
					isOpen={showConfirmAbrirParcela}
					title="Abrir Parcela"
					message="Deseja marcar esta parcela como aberta (não paga) e limpar a data de pagamento?"
					details={parcelaToToggleOpen ? [
						`Parcela: ${parcelaToToggleOpen?.numeroParcela ?? '-'}`,
						`Vencimento: ${parcelaToToggleOpen?.dataVencimento ? new Date(parcelaToToggleOpen.dataVencimento).toLocaleDateString('pt-BR') : '-'}`
					] : []}
					onConfirm={() => {
						// Clear payment info in draft for the parcela being toggled
						setParcelaDraft((d) => ({ ...(d || {}), pago: false, dataPagamento: undefined }));
						setShowConfirmAbrirParcela(false);
						setParcelaToToggleOpen(null);
					}}
					onCancel={() => {
						setShowConfirmAbrirParcela(false);
						setParcelaToToggleOpen(null);
					}}
					confirmText="Sim, abrir"
					cancelText="Cancelar"
					confirmButtonColor="#7A61B2"
				/>
			</>
		);
	};

	export default ApoliceFormModal;




